import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import InvoiceModal from './InvoiceModal'
import { fmtINR, fmtInvDate, invDate, normStatus } from '../lib/billing'
import { resolvePrescriber, regLine, printPrescription, buildRxWhatsApp } from '../lib/prescription'
import { friendlyError, PAST_BOOKING_MSG } from '../lib/permissions'
import {
  CLINIC_HOURS, CALENDAR_RANGE, toMins, toTime, todayIST, currentSlotMins, walkInTime, daySlots,
  isPastSlot, slotLabel, bookableSlots, nextAvailableSlot, tomorrowISO, normalizeTime,
} from '../config/clinicHours'

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  teal950: '#082828', teal900: '#0d3d3d', teal800: '#0f5454',
  teal700: '#126868', teal600: '#148080', teal500: '#19a8a8',
  teal400: '#2ecece', teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red600: '#dc2626',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

// ─── Status config — keys are EXACT DB CHECK constraint values ────────────────
// DB allows: 'Scheduled','Confirmed','Waiting','In Treatment','Completed','Cancelled','No Show'
const STATUS = {
  'Waiting':      { label: 'Waiting',      bg: '#fef9c3', color: '#854d0e', border: '#eab308', dot: '#ca8a04', sbBg: 'rgba(234,179,8,.2)',    sbColor: '#fde68a' },
  'Confirmed':    { label: 'Confirmed',    bg: '#dcfce7', color: '#15803d', border: '#22c55e', dot: '#16a34a', sbBg: 'rgba(34,197,94,.18)',   sbColor: '#86efac' },
  'In Treatment': { label: 'In Treatment', bg: '#dbeafe', color: '#1d4ed8', border: '#3b82f6', dot: '#2563eb', sbBg: 'rgba(59,130,246,.2)',   sbColor: '#bfdbfe' },
  'Completed':    { label: 'Completed',    bg: '#f1f5f9', color: '#475569', border: '#94a3b8', dot: '#94a3b8', sbBg: 'rgba(148,163,184,.15)', sbColor: '#cbd5e1' },
  'Cancelled':    { label: 'Cancelled',    bg: '#fee2e2', color: '#b91c1c', border: '#ef4444', dot: '#dc2626', sbBg: 'rgba(239,68,68,.18)',   sbColor: '#fca5a5' },
  'No Show':      { label: 'No Show',      bg: '#ffedd5', color: '#7c2d12', border: '#fdba74', dot: '#ea580c', sbBg: 'rgba(249,115,22,.18)',  sbColor: '#fed7aa' },
}
const ALL_STATUSES = Object.keys(STATUS)
// Safe lookup — handles legacy 'Scheduled' rows and any brief snake_case values
function getStatus(key) {
  const aliasMap = {
    'Scheduled':    'Waiting',       // legacy DB value
    'waiting':      'Waiting',       // snake_case (transient)
    'confirmed':    'Confirmed',
    'in_treatment': 'In Treatment',
    'completed':    'Completed',
    'cancelled':    'Cancelled',
    'no_show':      'No Show',
  }
  return STATUS[key] || STATUS[aliasMap[key]] || STATUS['Waiting']
}
// Dropdown options — exact DB strings, default is 'Waiting'
const STATUS_OPTIONS = [
  { label: 'Waiting',      value: 'Waiting'      },
  { label: 'Confirmed',    value: 'Confirmed'    },
  { label: 'In Treatment', value: 'In Treatment' },
  { label: 'Completed',    value: 'Completed'    },
  { label: 'Cancelled',    value: 'Cancelled'    },
  { label: 'No Show',      value: 'No Show'      },
]

const SERVICES_CONSULTATION = ['Consultation', 'Follow-up', 'Procedure', 'Treatment']
const SERVICES_PROCEDURES   = [
  'Facial Treatment', 'Chemical Peel', 'Laser Hair Removal',
  'Botox', 'Dermal Fillers', 'Microneedling', 'HydraFacial',
  'Acne Treatment', 'Pigmentation Treatment', 'PRP Hair Treatment',
  'Mole/Wart Removal', 'Skin Tag Removal', 'Skin Biopsy', 'Dermoscopy', 'Cryotherapy', 'Other',
]
const DURATION_OPTIONS = ['15 min', '30 min', '45 min', '1 hour', '1.5 hours', '2 hours']

// ─── Smart slot booking — service durations & buffer times ────────────────────
const SERVICE_DURATIONS = {
  'Consultation':           { duration: 20,  buffer: 10 },
  'Follow-up':              { duration: 15,  buffer: 5  },
  'Follow-up Consultation': { duration: 15,  buffer: 5  },
  'Chemical Peel':          { duration: 15,  buffer: 5  },
  'HydraFacial':            { duration: 45,  buffer: 15 },
  'Laser Treatment':        { duration: 90,  buffer: 20 },
  'Laser Hair Removal':     { duration: 90,  buffer: 20 },
  'Botox':                  { duration: 45,  buffer: 15 },
  'Dermal Fillers':         { duration: 45,  buffer: 15 },
  'Botox/Fillers':          { duration: 45,  buffer: 15 },
  'PRP Treatment':          { duration: 60,  buffer: 20 },
  'PRP Hair Treatment':     { duration: 60,  buffer: 20 },
  'Microneedling':          { duration: 60,  buffer: 15 },
  'Mole Removal':           { duration: 45,  buffer: 10 },
  'Wart Removal':           { duration: 45,  buffer: 10 },
  'Mole/Wart Removal':      { duration: 45,  buffer: 10 },
  'Acne Treatment':         { duration: 30,  buffer: 10 },
  'Skin Treatment':         { duration: 30,  buffer: 10 },
  'Acne/Skin Treatment':    { duration: 30,  buffer: 10 },
  'Dermoscopy':             { duration: 15,  buffer: 5  },
  'Patch Test':             { duration: 15,  buffer: 5  },
  'Dermoscopy/Patch Test':  { duration: 15,  buffer: 5  },
  'Cryotherapy':            { duration: 30,  buffer: 10 },
  'Other':                  { duration: 30,  buffer: 10 },
}
const DEFAULT_DURATION = { duration: 30, buffer: 10 }

// ─── Clinic schedule helpers ───────────────────────────────────────────────────

// Overlaps never block booking — they are confirmed with "Book anyway" (override_booking).
// Cancelled / no-show appointments don't count.
function checkSlotConflict(date, time, service, appointments) {
  const { duration, buffer } = SERVICE_DURATIONS[service] || DEFAULT_DURATION
  const slotStart = toMins(time)
  const slotEnd   = slotStart + duration + buffer
  const overlaps = (appointments || []).filter(appt => {
    const d = appt.date || (appt.start_time ? appt.start_time.split('T')[0] : null)
    if (d !== date || !appt.time) return false
    if (['Cancelled', 'No Show'].includes(appt.status)) return false
    const svc     = appt.service_type || appt.appointment_type || 'Other'
    const apptDur = SERVICE_DURATIONS[svc] || DEFAULT_DURATION
    const aStart  = toMins((appt.time || '00:00').slice(0, 5))
    const aEnd    = aStart + apptDur.duration + apptDur.buffer
    return slotStart < aEnd && slotEnd > aStart
  })
  return overlaps.length
    ? { conflict: true, reason: 'booked', conflictWith: overlaps[0], overlaps }
    : { conflict: false, overlaps: [] }
}

function findNextAvailableSlots(date, service, appointments, count = 3, startAfter = null) {
  const clinicStart = toMins(CALENDAR_RANGE.start)
  const clinicEnd   = toMins(CALENDAR_RANGE.end)
  const { duration, buffer } = SERVICE_DURATIONS[service] || DEFAULT_DURATION

  // Start searching AFTER the conflict slot, not from clinic open
  let searchFrom = clinicStart
  if (startAfter) searchFrom = toMins(startAfter) + 1

  // Never suggest past times (IST): today starts from the current slot, past days get nothing
  const todayStr = todayIST()
  if (date < todayStr) return []
  if (date === todayStr) searchFrom = Math.max(searchFrom, currentSlotMins())

  const available = []
  const step = CLINIC_HOURS.slotMinutes
  const firstSlot = Math.ceil(searchFrom / step) * step
  for (let slot = firstSlot; slot + duration + buffer <= clinicEnd; slot += step) {
    if (!checkSlotConflict(date, toTime(slot), service, appointments).conflict) {
      available.push(toTime(slot))
      if (available.length >= count) break
    }
  }
  return available
}

// ─── Visual slot timeline ──────────────────────────────────────────────────────
function SlotTimeline({ date, service, appointments, selectedTime, onTimeClick, allowPast }) {
  const startMins  = toMins(CALENDAR_RANGE.start)
  const endMins    = toMins(CALENDAR_RANGE.end)
  const totalMins  = endMins - startMins
  const { duration, buffer } = SERVICE_DURATIONS[service] || DEFAULT_DURATION
  const selStart   = toMins(selectedTime)

  const bookedRanges = (appointments || [])
    .filter(a => a.date === date && a.time)
    .map(a => {
      const s   = toMins((a.time || '00:00').slice(0, 5))
      const svc = a.service_type || a.appointment_type || 'Other'
      const d   = SERVICE_DURATIONS[svc] || DEFAULT_DURATION
      return { start: s, durEnd: s + d.duration, bufEnd: s + d.duration + d.buffer, appt: a }
    })

  const STEP = 15
  const slots = Math.floor(totalMins / STEP)
  const hourMarks = []
  for (let m = startMins; m <= endMins; m += 60) hourMarks.push(m)

  const SEL_COLOR    = '#3b82f6'   // strong blue — selected duration
  const SEL_BUF_COLOR = '#93c5fd'  // medium blue — selected buffer
  const AVAIL_COLOR  = '#bbf7d0'   // green — available
  const BOOKED_COLOR = '#fb923c'   // orange — booked (still clickable: overlaps are allowed)
  const BUF_COLOR    = '#fed7aa'   // light orange — booked buffer
  const PAST_COLOR   = '#e5e7eb'   // gray — already passed (the only blocked state)

  function segColor(m) {
    // Selected slot takes highest priority (renders on top of everything)
    if (selStart >= startMins && m >= selStart && m < selStart + duration) return SEL_COLOR
    if (selStart >= startMins && m >= selStart + duration && m < selStart + duration + buffer) return SEL_BUF_COLOR
    // Already-passed time (not bookable unless recording a past visit)
    if (!allowPast && isPastSlot(date, toTime(m))) return PAST_COLOR
    // Booked appointments
    for (const r of bookedRanges) {
      if (m >= r.start && m < r.durEnd) return BOOKED_COLOR
      if (m >= r.durEnd && m < r.bufEnd) return BUF_COLOR
    }
    return AVAIL_COLOR
  }

  const SEG_LEGEND = [
    [AVAIL_COLOR,   'Available'],
    [SEL_COLOR,     'Selected'],
    [SEL_BUF_COLOR, 'Sel. buffer'],
    [BOOKED_COLOR,  'Booked'],
    [BUF_COLOR,     'Buffer'],
    [PAST_COLOR,    'Past'],
  ]

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 5 }}>
        Day Timeline
        <span style={{ marginLeft: 10, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          {SEG_LEGEND.map(([bg, label]) => (
            <span key={label} style={{ marginRight: 8, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: bg, display: 'inline-block', border: '1px solid rgba(0,0,0,0.12)' }} />{label}
            </span>
          ))}
        </span>
      </div>
      <div style={{ height: 36, borderRadius: 8, overflow: 'hidden', display: 'flex', border: '1.5px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        {Array.from({ length: slots }, (_, i) => {
          const m      = startMins + i * STEP
          const color  = segColor(m)
          const isOpen = color !== PAST_COLOR   // booked segments stay clickable
          const label  = slotLabel(toTime(m))
          const isSel  = color === SEL_COLOR || color === SEL_BUF_COLOR
          const book   = bookedRanges.find(r => m >= r.start && m < r.bufEnd)
          const tip    = book
            ? `${book.appt.patients?.full_name || book.appt.patient_name || '?'} · ${book.appt.service_type || book.appt.appointment_type || '—'} · ${fmtTime((book.appt.time||'00:00').slice(0,5))}`
            : isSel ? `Selected: ${fmtTime(toTime(selStart))}`
            : isOpen ? `Book ${fmtTime(toTime(m))}${label ? ` (${label})` : ''}` : 'Past time'
          return (
            <div key={m} title={tip}
              onClick={() => isOpen && onTimeClick(toTime(m))}
              style={{
                flex: 1, background: color, cursor: isOpen ? 'pointer' : 'not-allowed',
                backgroundImage: label && color === AVAIL_COLOR ? 'repeating-linear-gradient(135deg, rgba(255,255,255,.55) 0 3px, transparent 3px 6px)' : undefined,
                borderRight: i < slots - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                transition: 'filter .1s',
                outline: isSel && color === SEL_COLOR ? '0' : undefined,
              }}
              onMouseEnter={e => { if (isOpen) e.currentTarget.style.filter = 'brightness(0.88)' }}
              onMouseLeave={e => { e.currentTarget.style.filter = '' }}
            />
          )
        })}
      </div>
      <div style={{ display: 'flex', marginTop: 3, position: 'relative' }}>
        {hourMarks.map(m => (
          <div key={m} style={{ position: 'absolute', left: `${((m - startMins) / totalMins) * 100}%`, fontSize: 9, color: '#94a3b8', fontWeight: 500, transform: 'translateX(-50%)' }}>
            {fmtTime(toTime(m))}
          </div>
        ))}
      </div>
      <div style={{ height: 14 }} />
    </div>
  )
}

// ─── Time slots 09:00 → 19:30 ─────────────────────────────────────────────────
// Calendar rows: CALENDAR_RANGE (8:00 AM – 10:00 PM) in CLINIC_HOURS.slotMinutes steps
const TIME_SLOTS = daySlots()
const TIME_W  = 60   // px — must be identical on corner, time labels, and column template
const DAY_MIN = 130  // min px per day column

// ─── Helpers ───────────────────────────────────────────────────────────────────
function fmtTime(t) {
  if (!t) return ''
  const trimmed = t.trim()
  // Already in 12-hour format (e.g. "2:15 PM") — return as-is
  if (trimmed.includes('AM') || trimmed.includes('PM')) return trimmed
  const [h, m] = trimmed.split(':')
  const hr = parseInt(h, 10)
  return `${hr > 12 ? hr - 12 : hr || 12}:${(m || '00').slice(0, 2)} ${hr < 12 ? 'AM' : 'PM'}`
}
// Parses a time string (24hr "14:15" or 12hr "2:15 PM") and returns the
// nearest preceding 30-min TIME_SLOT string ("14:00"). Returns null if unparseable.
function timeToSlot(timeStr) {
  if (!timeStr) return null
  const t = timeStr.trim()
  let hours, minutes
  if (t.includes('AM') || t.includes('PM')) {
    const [timePart, period] = t.split(' ')
    ;[hours, minutes] = timePart.split(':').map(Number)
    if (period === 'PM' && hours !== 12) hours += 12
    if (period === 'AM' && hours === 12) hours = 0
  } else {
    ;[hours, minutes] = t.split(':').map(Number)
  }
  if (isNaN(hours) || isNaN(minutes)) return null
  const step = CLINIC_HOURS.slotMinutes
  return toTime(hours * 60 + Math.floor(minutes / step) * step)
}
function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
function isoDate(d) {
  // MUST use local date parts — toISOString() returns UTC, which at midnight IST
  // gives yesterday's date, breaking day-view cell matching and the weekly query.
  const yyyy = d.getFullYear()
  const mm   = String(d.getMonth() + 1).padStart(2, '0')
  const dd   = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
function isToday(d) { return isoDate(d) === isoDate(new Date()) }
function avatarInitials(name) {
  return (name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}

function getMondayOfWeek(date) {
  const d = new Date(date)
  const day = d.getDay()
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day))
  d.setHours(0, 0, 0, 0)
  return d
}
function getWeekDays(monday) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(d.getDate() + i); return d
  })
}
// ─── Shared input styles ───────────────────────────────────────────────────────
const sx = {
  width: '100%', padding: '9px 12px', border: `1.5px solid ${T.gray200}`,
  borderRadius: 8, fontSize: 13.5, color: T.gray900, background: T.gray50,
  outline: 'none', fontFamily: T.font, boxSizing: 'border-box',
}
function FInput({ value, onChange, placeholder, type = 'text', onFocus, onBlur, onKeyDown, style }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      onKeyDown={onKeyDown}
      style={{ ...sx, ...style }}
      onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}`; onFocus && onFocus(e) }}
      onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none'; onBlur && onBlur(e) }}
    />
  )
}
function FSelect({ value, onChange, children, id }) {
  return (
    <select id={id} value={value} onChange={onChange} style={{ ...sx, cursor: 'pointer' }}
      onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
      onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }}
    >{children}</select>
  )
}
function FLabel({ children }) {
  return <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.gray500, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{children}</label>
}
function FField({ label, children, half }) {
  return (
    <div style={{ marginBottom: 14, width: half ? 'calc(50% - 6px)' : '100%' }}>
      <FLabel>{label}</FLabel>
      {children}
    </div>
  )
}

// PatientSearch removed — BookingModal uses simple name+phone fields with find-or-create

// ─── Doctor field ──────────────────────────────────────────────────────────────
// Doctors come from user_profiles (registered doctors in this clinic) via AuthContext.
const LAST_DOCTOR_KEY = 'md_last_doctor'
function DoctorField({ value, onChange }) {
  const { doctors } = useAuth()
  const names = doctors.map(d => d.full_name)

  useEffect(() => {
    if (value || !names.length) return
    const last = localStorage.getItem(LAST_DOCTOR_KEY) || ''
    onChange(names.includes(last) ? last : names[0])
  }, [names.join('|')]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(v) { onChange(v); if (v) localStorage.setItem(LAST_DOCTOR_KEY, v) }

  return (
    <FSelect value={value} onChange={e => handleChange(e.target.value)}>
      <option value="">{names.length ? 'Select doctor…' : 'No registered doctors found'}</option>
      {names.map(n => <option key={n} value={n}>{n}</option>)}
      {/* Older appointments may carry a name that is no longer in the list */}
      {value && !names.includes(value) && <option value={value}>{value}</option>}
    </FSelect>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// BOOKING MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function BookingModal({ initialDate, initialTime, initialPast = false, onClose, onSaved, editAppt, allAppointments = [] }) {
  const { userProfile, can, isAdmin } = useAuth()
  // Existing appointment notes may hold doctors' clinical notes; the value is kept unchanged on save
  const showNotes = !editAppt || can('clinical.view')
  const isEdit = Boolean(editAppt)
  // Family members may share one phone number — let the receptionist pick who this is for
  const [phoneMatches, setPhoneMatches] = useState([])
  const [pickedId, setPickedId]         = useState('') // '' = not chosen, 'new' = new patient, else patient id
  const [patientName,  setPatientName]  = useState(editAppt?.patients?.full_name || '')
  const [patientPhone, setPatientPhone] = useState(editAppt?.patients?.phone || '')
  const [patientAge,   setPatientAge]   = useState(editAppt?.patients?.age   || '')
  const [patientGender,setPatientGender]= useState(editAppt?.patients?.gender || '')
  // Admin only: allow a date/time in the past to record a visit that already happened
  const [pastVisit, setPastVisit] = useState(!editAppt && isAdmin && initialPast)
  const [form, setForm]   = useState(() => {
    if (editAppt) {
      return {
        appointment_type: editAppt.appointment_type || '',
        service_type:     editAppt.service_type     || '',
        doctor:           editAppt.doctor           || '',
        status:           editAppt.status           || 'Waiting',
        notes:            editAppt.notes            || '',
        date:             editAppt.date,
        time:             normalizeTime(editAppt.time) || nextAvailableSlot(editAppt.date) || CLINIC_HOURS.open,
        duration:         editAppt.duration         || '',
      }
    }
    // New booking: use the clicked slot exactly; otherwise the next free slot (tomorrow if today is over)
    const allowPastInit = isAdmin && initialPast
    let date = initialDate || todayIST()
    const clicked = normalizeTime(initialTime)
    let time = clicked && bookableSlots(date, { allowPast: allowPastInit }).includes(clicked) ? clicked : null
    if (!time) time = allowPastInit ? (clicked || CLINIC_HOURS.open) : nextAvailableSlot(date)
    if (!time) { date = tomorrowISO(todayIST()); time = nextAvailableSlot(date) }
    return {
      appointment_type: '', service_type: '', doctor: '', status: 'Waiting', notes: '',
      date, time: time || CLINIC_HOURS.open, duration: '',
    }
  })
  const [toast, setToast]         = useState('')
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState('')
  const [conflict, setConflict]   = useState(null)
  const [override, setOverride]   = useState(false)
  const [suggestions, setSugg]    = useState([])

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  // Past times are allowed for admins recording a past visit, or when an existing
  // appointment's date/time is left unchanged.
  const unchangedEdit = isEdit && form.date === editAppt.date && form.time === normalizeTime(editAppt.time)
  const allowPast     = (isAdmin && pastVisit) || unchangedEdit
  const selectedPast  = isPastSlot(form.date, form.time)
  const timeOptions   = (() => {
    const opts = bookableSlots(form.date, { allowPast })
    // keep an existing / walk-in value visible even if it sits outside the normal list
    return form.time && !opts.includes(form.time) && (allowPast || !selectedPast) ? [...opts, form.time].sort() : opts
  })()
  const minDate = allowPast ? undefined : (isEdit && editAppt.date < todayIST() ? editAppt.date : todayIST())

  // If the date changes (or past-visit is unticked) and the chosen time is no longer valid, move to the first valid slot
  useEffect(() => {
    if (unchangedEdit) return
    if (!allowPast && form.date < todayIST()) { set('date', todayIST()); return }
    if (!timeOptions.includes(form.time)) {
      const first = timeOptions[0]
      if (first) set('time', first)
    }
  }, [form.date, allowPast]) // eslint-disable-line react-hooks/exhaustive-deps

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 6000) }

  function setWalkIn() {
    setPastVisit(false)
    setForm(f => ({ ...f, date: todayIST(), time: walkInTime(), status: 'Waiting' }))
  }

  // When editing, always fetch the latest patient record so age/gender/phone are correct
  useEffect(() => {
    if (!isEdit || !editAppt?.patient_id) return
    supabase.from('patients').select('age, gender, phone, full_name')
      .eq('id', editAppt.patient_id).single()
      .then(({ data }) => {
        if (!data) return
        if (data.age    != null)    setPatientAge(String(data.age))
        if (data.gender)            setPatientGender(data.gender)
        if (data.phone)             setPatientPhone(data.phone)
        if (data.full_name)         setPatientName(data.full_name)
      })
  }, [editAppt?.patient_id, isEdit])

  // Look up every patient already using this phone number (new bookings only)
  useEffect(() => {
    if (isEdit) return
    const digits = patientPhone.replace(/\D/g, '')
    setPickedId('')
    if (digits.length < 10) { setPhoneMatches([]); return }
    const last10 = digits.slice(-10)
    const t = setTimeout(async () => {
      const { data } = await supabase.from('patients')
        .select('id, full_name, phone, age, gender').ilike('phone', `%${last10}`).order('full_name')
      const list = data || []
      setPhoneMatches(list)
      // Pre-select when the typed name matches exactly one of them
      const typed = patientName.trim().toLowerCase()
      const same  = list.filter(p => (p.full_name || '').trim().toLowerCase() === typed)
      if (same.length === 1) setPickedId(same[0].id)
    }, 350)
    return () => clearTimeout(t)
  }, [patientPhone, isEdit]) // eslint-disable-line react-hooks/exhaustive-deps

  function pickPatient(p) {
    if (p === 'new') { setPickedId('new'); return }
    setPickedId(p.id)
    if (p.full_name) setPatientName(p.full_name)
    setPatientAge(p.age != null ? String(p.age) : '')
    setPatientGender(p.gender || '')
  }

  // Re-run conflict check whenever date, time, or service changes
  useEffect(() => {
    if (!form.date || !form.time) { setConflict(null); setSugg([]); return }
    const svc = form.service_type || form.appointment_type || 'Other'
    // Exclude the appointment being edited from conflict checks
    const others = isEdit ? allAppointments.filter(a => a.id !== editAppt.id) : allAppointments
    const result = checkSlotConflict(form.date, form.time, svc, others)
    setConflict(result)
    if (result.conflict) {
      // Compute end time of the conflicting appointment so suggestions
      // start AFTER it, not from clinic open
      let startAfter = null
      if (result.conflictWith) {
        const cw     = result.conflictWith
        const cwSvc  = cw.service_type || cw.appointment_type || 'Other'
        const cwDur  = SERVICE_DURATIONS[cwSvc] || DEFAULT_DURATION
        startAfter   = toTime(toMins((cw.time || '00:00').slice(0, 5)) + cwDur.duration + cwDur.buffer)
      }
      setSugg(findNextAvailableSlots(form.date, svc, others, 3, startAfter))
    } else setSugg([])
    setOverride(false)   // a new time needs its own "Book anyway" confirmation
  }, [form.date, form.time, form.service_type, form.appointment_type]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave(forceOverride = false) {
    const name  = patientName.trim()
    const phone = patientPhone.trim() || null
    if (!name)                   { setError('Please enter a patient name.'); return }
    if (!form.appointment_type)  { setError('Please select an appointment type.'); return }
    if (!form.doctor)            { setError('Please enter or select a doctor.'); return }
    if (!allowPast && selectedPast) { setError(''); showToast(PAST_BOOKING_MSG); return }
    const overrideOk = override || forceOverride
    if (conflict?.conflict && !overrideOk) { setError('This time already has a booking — choose "Book anyway" or pick another time.'); return }
    if (!isEdit && phoneMatches.length > 0 && !pickedId) {
      setError('This phone number is already registered. Please choose which patient the appointment is for, or select "New patient".')
      return
    }
    const creatingPatient = !isEdit && (!phoneMatches.length || pickedId === 'new')
    if (creatingPatient && !can('patients.add')) {
      setError(phoneMatches.length
        ? 'Only reception can register a new patient. Please pick an existing patient.'
        : 'No patient found with this phone number. Only reception can register new patients.')
      return
    }
    setError(''); setSaving(true)
    try {
      const svc      = form.service_type || form.appointment_type || 'Other'
      const durCfg   = SERVICE_DURATIONS[svc] || DEFAULT_DURATION
      const apptPayload = {
        date: form.date, time: form.time,
        appointment_type: form.appointment_type,
        service_type:     form.service_type || null,
        doctor: form.doctor, status: form.status, notes: form.notes || null,
        duration: form.duration || null,
        duration_mins:    durCfg.duration,
        buffer_mins:      durCfg.buffer,
        override_booking: !!(conflict?.conflict && overrideOk),
      }
      if (isEdit) {
        // UPDATE patient record FIRST — phone/name/age/gender live in patients table
        if (editAppt.patient_id) {
          const patUpdates = {
            full_name: name || undefined,
            phone:     phone || null,
            age:       patientAge ? parseInt(patientAge, 10) : null,
            gender:    patientGender || null,
          }
          await supabase.from('patients').update(patUpdates).eq('id', editAppt.patient_id)
        }
        // UPDATE appointment — select joins patients so returned data reflects new phone/name
        const { data, error: err } = await supabase.from('appointments')
          .update(apptPayload).eq('id', editAppt.id)
          .select('*, patients(full_name, phone, age, gender)').single()
        if (err) throw err
        onSaved(data)
      } else {
        // INSERT new appointment — use the patient picked for this phone, or create one
        let patient = null
        if (pickedId && pickedId !== 'new') {
          patient = phoneMatches.find(p => p.id === pickedId) || null
        }
        if (!patient) {
          const insertData = { full_name: name, phone, clinic_id: userProfile?.clinic_id }
          if (patientAge)    insertData.age    = parseInt(patientAge, 10)
          if (patientGender) insertData.gender = patientGender
          const { data: created, error: ce } = await supabase.from('patients')
            .insert(insertData).select('id, full_name, phone').single()
          if (ce) throw ce
          patient = created
        } else if (patientAge || patientGender) {
          // Update existing patient with any new demographic info
          const updates = {}
          if (patientAge)    updates.age    = parseInt(patientAge, 10)
          if (patientGender) updates.gender = patientGender
          await supabase.from('patients').update(updates).eq('id', patient.id)
        }
        const { data, error: err } = await supabase.from('appointments')
          .insert({ patient_id: patient.id, clinic_id: userProfile?.clinic_id, ...apptPayload })
          .select('*, patients(full_name, phone)').single()
        if (err) throw err
        onSaved(data)
      }
    } catch (e) {
      const msg = friendlyError(e, 'Save failed')
      // The database rejects past bookings by reception — show that as a toast, not a raw error
      if (msg === PAST_BOOKING_MSG) { setError(''); showToast(msg) } else setError(msg)
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20, fontFamily: T.font }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 540, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', background: T.teal900, borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.white }}>{isEdit ? 'Edit Appointment' : 'New Appointment'}</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.teal200 }}>
              {isEdit ? `Editing: ${editAppt.patients?.full_name || 'Patient'}` : `${fmtDate(form.date)} · ${fmtTime(form.time)}`}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, color: T.white, width: 32, height: 32, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        {toast && (
          <div role="alert" style={{ position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#fff7ed', border: '1.5px solid #fdba74', color: '#9a3412', borderRadius: 10, padding: '12px 18px', fontSize: 13.5, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,.18)', display: 'flex', gap: 10, alignItems: 'center', maxWidth: 480 }}>
            <span>⏰ {toast}</span>
            <button onClick={() => setToast('')} style={{ background: 'none', border: 'none', color: '#9a3412', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        )}
        <div style={{ overflowY: 'auto', padding: '22px 24px' }}>
          {!isEdit && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button type="button" onClick={setWalkIn} style={{ padding: '6px 14px', borderRadius: 8, border: `1.5px solid ${T.teal500}`, background: T.teal50, color: T.teal700, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>
                🚶 Walk-in (now)
              </button>
            </div>
          )}
          {error && <div style={{ background: T.red50, border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', color: T.red600, fontSize: 13, marginBottom: 16 }}>⚠️ {error}</div>}
          <div style={{ display: 'flex', gap: 12 }}>
            <FField label="Patient Name *" half>
              <FInput value={patientName} onChange={e => setPatientName(e.target.value)} placeholder="Full name" />
            </FField>
            <FField label="Phone Number" half>
              <FInput value={patientPhone} onChange={e => setPatientPhone(e.target.value)} placeholder="10-digit mobile" />
            </FField>
          </div>
          {!isEdit && phoneMatches.length > 0 && (
            <div style={{ background: T.teal50, border: `1.5px solid ${T.teal200}`, borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.teal700, marginBottom: 8 }}>
                {phoneMatches.length === 1 ? '1 patient already uses this number' : `${phoneMatches.length} patients already use this number`} — who is this appointment for?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {phoneMatches.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.gray700, cursor: 'pointer' }}>
                    <input type="radio" name="phone-match" checked={pickedId === p.id} onChange={() => pickPatient(p)} style={{ accentColor: T.teal600 }} />
                    <strong style={{ color: T.gray900 }}>{p.full_name}</strong>
                    <span style={{ color: T.gray500 }}>{[p.age != null ? `${p.age} yrs` : null, p.gender].filter(Boolean).join(' · ') || '—'}</span>
                  </label>
                ))}
                {can('patients.add') && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.gray700, cursor: 'pointer' }}>
                    <input type="radio" name="phone-match" checked={pickedId === 'new'} onChange={() => pickPatient('new')} style={{ accentColor: T.teal600 }} />
                    ➕ New patient with this number (e.g. family member)
                  </label>
                )}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <FField label="Age" half>
              <FInput type="number" value={patientAge} onChange={e => setPatientAge(e.target.value)} placeholder="e.g. 32" style={{ appearance: 'textfield' }} />
            </FField>
            <FField label="Gender" half>
              <FSelect value={patientGender} onChange={e => setPatientGender(e.target.value)}>
                <option value="">Select…</option>
                {['Male', 'Female', 'Other', 'Prefer not to say'].map(g => <option key={g} value={g}>{g}</option>)}
              </FSelect>
            </FField>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <FField label="Date *" half>
              <input type="date" value={form.date} min={minDate}
                onChange={e => { if (e.target.value) set('date', e.target.value) }}
                style={sx} />
            </FField>
            <FField label="Time *" half>
              <FSelect id="booking-time" value={form.time} onChange={e => set('time', e.target.value)}>
                {timeOptions.length === 0 && <option value={form.time}>No slots left today</option>}
                {timeOptions.map(s => (
                  <option key={s} value={s}>{fmtTime(s)}{slotLabel(s) ? ` · ${slotLabel(s)}` : ''}</option>
                ))}
              </FSelect>
            </FField>
          </div>
          {!isEdit && isAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.gray600, marginTop: -6, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={pastVisit} onChange={e => setPastVisit(e.target.checked)} style={{ accentColor: T.teal600 }} />
              Record a past visit <span style={{ color: T.gray400 }}>(allows earlier dates and times — admin only)</span>
            </label>
          )}
          {!allowPast && form.date === todayIST() && timeOptions.length === 0 && (
            <div style={{ fontSize: 12, color: '#b45309', marginTop: -6, marginBottom: 12 }}>No more slots today — please pick another date.</div>
          )}

          {/* ── Slot status + visual timeline ── */}
          {form.date && form.time && (() => {
            const svc = form.service_type || form.appointment_type || 'Other'
            const others = isEdit ? allAppointments.filter(a => a.id !== editAppt.id) : allAppointments
            return (
              <>
                {selectedPast ? (
                  <div style={{ fontSize: 12, color: T.gray500, fontWeight: 600, marginBottom: 10 }}>
                    {allowPast ? '🕘 Past time — this will be recorded as a past visit.' : '⏰ This time has already passed. Choose a current or future slot.'}
                  </div>
                ) : conflict?.conflict ? (() => {
                  const list = conflict.overlaps || []
                  const n    = list.length
                  const who  = list.map(a => `${a.patients?.full_name || a.patient_name || 'Patient'} – ${a.service_type || a.appointment_type || 'Appointment'}`).join(', ')
                  return (
                    <div style={{ background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', marginBottom: 4 }}>
                        ⚠️ Slot has {n} other booking{n !== 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: 12.5, color: '#92400e', marginBottom: 10 }}>
                        {fmtTime(form.time)} already has {n} appointment{n !== 1 ? 's' : ''} ({who}). Book anyway?
                      </div>
                      {override ? (
                        <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>✓ Booking anyway — this appointment will be marked as an overlap.</div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <button type="button" disabled={saving} onClick={() => { setOverride(true); handleSave(true) }} style={{
                            padding: '6px 14px', borderRadius: 8, border: 'none', background: '#d97706', color: T.white,
                            fontSize: 12.5, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: T.font,
                          }}>Book anyway</button>
                          <button type="button" onClick={() => { if (suggestions[0]) set('time', suggestions[0]); else document.getElementById('booking-time')?.focus() }} style={{
                            padding: '6px 14px', borderRadius: 8, border: '1.5px solid #fcd34d', background: T.white, color: '#92400e',
                            fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: T.font,
                          }}>Choose another time</button>
                          {suggestions.length > 0 && (
                            <span style={{ fontSize: 11, color: T.gray500, display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                              Free:
                              {suggestions.map(t => (
                                <button key={t} type="button" onClick={() => set('time', t)} style={{
                                  padding: '2px 10px', borderRadius: 20, border: `1.5px solid ${T.teal500}`,
                                  background: T.teal50, color: T.teal700, fontSize: 11.5, fontWeight: 700,
                                  cursor: 'pointer', fontFamily: T.font,
                                }}>{fmtTime(t)}</button>
                              ))}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })() : (
                  <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontSize: 14 }}>✓</span> Slot available
                    {slotLabel(form.time) && <span style={{ fontWeight: 500, color: T.gray400 }}>· {slotLabel(form.time)}</span>}
                  </div>
                )}
                <SlotTimeline
                  date={form.date} service={svc}
                  appointments={others}
                  selectedTime={form.time}
                  allowPast={allowPast}
                  onTimeClick={t => set('time', t)}
                />
              </>
            )
          })()}

          <div style={{ display: 'flex', gap: 12 }}>
            <FField label="Appointment Type *" half>
              <FSelect value={form.appointment_type} onChange={e => set('appointment_type', e.target.value)}>
                <option value="">Select type…</option>
                {SERVICES_CONSULTATION.map(s => <option key={s} value={s}>{s}</option>)}
              </FSelect>
            </FField>
            <FField label="Specific Service" half>
              <FSelect value={form.service_type} onChange={e => set('service_type', e.target.value)}>
                <option value="">Select service…</option>
                {SERVICES_PROCEDURES.map(s => <option key={s} value={s}>{s}</option>)}
              </FSelect>
            </FField>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <FField label="Doctor *" half><DoctorField value={form.doctor} onChange={v => set('doctor', v)} /></FField>
            <FField label="Duration" half>
              <FSelect value={form.duration} onChange={e => set('duration', e.target.value)}>
                <option value="">Select…</option>
                {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </FSelect>
            </FField>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <FField label="Status" half><FSelect value={form.status} onChange={e => set('status', e.target.value)}>{STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</FSelect></FField>
          </div>
          {showNotes && <FField label="Notes">
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes about this appointment…" rows={3}
              style={{ ...sx, resize: 'vertical' }}
              onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
              onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }} />
          </FField>}
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.gray100}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
          <button onClick={() => handleSave()} disabled={saving} style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: saving ? T.teal200 : T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: T.font }}>
            {saving ? (isEdit ? 'Saving…' : 'Booking…') : (isEdit ? 'Save Changes' : 'Book Appointment')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Skin condition colours (prototype COND_COLORS) ───────────────────────────
const COND_COLORS = {
  'Acne':          { c:'#c2410c', bg:'#ffedd5' },
  'Pigmentation':  { c:'#7c3aed', bg:'#f5f3ff' },
  'Eczema':        { c:'#1d4ed8', bg:'#dbeafe' },
  'Psoriasis':     { c:'#be123c', bg:'#ffe4e6' },
  'Melasma':       { c:'#854d0e', bg:'#fef9c3' },
  'Rosacea':       { c:'#9d174d', bg:'#fdf2f8' },
  'Hair Loss':     { c:'#065f46', bg:'#d1fae5' },
  'Chemical Peel': { c:'#0369a1', bg:'#e0f2fe' },
  'Anti-aging':    { c:'#6b21a8', bg:'#f3e8ff' },
  'Dermatitis':    { c:'#b45309', bg:'#fef3c7' },
  'Vitiligo':      { c:'#475569', bg:'#f1f5f9' },
  'Fungal':        { c:'#15803d', bg:'#dcfce7' },
}

// ─── Body areas for photo metadata ────────────────────────────────────────────
const BODY_AREAS = ['Face', 'Forehead', 'Cheek', 'Neck', 'Back', 'Arms', 'Scalp', 'Other']

// ─── Resolves a photo_url to a displayable src ────────────────────────────────
// Backward-compatible: existing base64 data URIs are used as-is; storage paths
// get a 1-hour signed URL generated on mount.
function PhotoImg({ photo_url, alt, style, ...rest }) {
  const [src, setSrc] = useState(null)
  useEffect(() => {
    if (!photo_url) return
    if (photo_url.startsWith('data:')) { setSrc(photo_url); return }
    supabase.storage.from('patient-photos').createSignedUrl(photo_url, 3600)
      .then(({ data }) => { if (data?.signedUrl) setSrc(data.signedUrl) })
  }, [photo_url])
  return <img src={src || ''} alt={alt} style={style} {...rest} />
}

// ═══════════════════════════════════════════════════════════════════════════════
// PHOTO UPLOAD MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function PhotoUploadModal({ patientId, onClose, onSaved }) {
  const { userProfile } = useAuth()
  const [file,      setFile]      = useState(null)
  const [preview,   setPreview]   = useState(null)
  const [bodyArea,  setBodyArea]  = useState('')
  const [visitDate, setVisitDate] = useState(isoDate(new Date()))
  const [photoNote, setPhotoNote] = useState('')
  const [saving,    setSaving]    = useState(false)
  const inputRef = useRef(null)

  function handleFile(e) {
    const f = e.target.files?.[0]
    if (!f) return
    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp']
    if (!ALLOWED.includes(f.type)) { alert('Only JPG, PNG or WebP images are allowed'); return }
    if (f.size > 5 * 1024 * 1024) { alert('Photo must be under 5 MB'); return }
    setFile(f)
    setPreview(URL.createObjectURL(f))
    e.target.value = ''
  }

  async function handleSave() {
    if (!file) return
    setSaving(true)
    const ext = file.type === 'image/jpeg' ? 'jpg' : file.type === 'image/png' ? 'png' : 'webp'
    const path = `${patientId}/${Date.now()}.${ext}`
    const { error: storageErr } = await supabase.storage.from('patient-photos').upload(path, file)
    if (storageErr) { alert('Upload failed: ' + storageErr.message); setSaving(false); return }
    const { data, error } = await supabase.from('patient_photos')
      .insert({ patient_id: patientId, photo_url: path, clinic_id: userProfile?.clinic_id, body_area: bodyArea, visit_date: visitDate, notes: photoNote })
      .select('*').single()
    if (!error && data) { onSaved(data); return }
    alert('Upload failed: ' + (error?.message || 'Unknown error'))
    setSaving(false)
  }

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.65)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: T.font }}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.35)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', background: T.teal900, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.white }}>📷 Add Skin Photo</span>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(255,255,255,.14)', border: 'none', color: T.white, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          {/* Drop zone or preview */}
          {!preview ? (
            <div
              onClick={() => inputRef.current?.click()}
              style={{ border: `2px dashed ${T.teal400}`, borderRadius: 12, padding: '40px 20px', textAlign: 'center', cursor: 'pointer', background: T.teal50, transition: 'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.teal600; e.currentTarget.style.background = T.teal100 }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.teal400; e.currentTarget.style.background = T.teal50 }}
            >
              <div style={{ fontSize: 38, marginBottom: 8 }}>📷</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.teal700, marginBottom: 4 }}>Click to select photo</div>
              <div style={{ fontSize: 12, color: T.gray400 }}>JPG, PNG or WebP</div>
            </div>
          ) : (
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 10, border: `1px solid ${T.gray200}` }} />
              <button onClick={() => { setPreview(null); setFile(null) }} style={{ position: 'absolute', top: 8, right: 8, width: 26, height: 26, borderRadius: 13, background: 'rgba(0,0,0,.6)', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
          )}
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleFile} />

          {/* Fields */}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <div style={{ flex: 1 }}>
              <FLabel>Body Area</FLabel>
              <FSelect value={bodyArea} onChange={e => setBodyArea(e.target.value)}>
                <option value="">Select area…</option>
                {BODY_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
              </FSelect>
            </div>
            <div style={{ flex: 1 }}>
              <FLabel>Visit Date</FLabel>
              <FInput type="date" value={visitDate} onChange={e => setVisitDate(e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <FLabel>Clinical Note</FLabel>
            <textarea value={photoNote} onChange={e => setPhotoNote(e.target.value)} placeholder="Brief note about this photo…" rows={2}
              style={{ ...sx, resize: 'vertical' }}
              onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
              onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.gray100}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
          <button onClick={handleSave} disabled={!file || saving}
            style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: !file || saving ? T.teal200 : T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: !file || saving ? 'default' : 'pointer', fontFamily: T.font }}>
            {saving ? 'Saving…' : 'Save Photo'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// PHOTO LIGHTBOX VIEWER
// ═══════════════════════════════════════════════════════════════════════════════
function PhotoLightbox({ photos, initialIndex, patientName, onClose, onSaved, onDeleted }) {
  const { isAdmin, can } = useAuth()  // delete: admin only · edit details: admin/doctor
  const [idx,       setIdx]       = useState(initialIndex)
  const [bodyArea,  setBodyArea]  = useState('')
  const [visitDate, setVisitDate] = useState('')
  const [photoNote, setPhotoNote] = useState('')
  const [saving,    setSaving]    = useState(false)
  const [savedMsg,  setSavedMsg]  = useState(false)
  const [deleting,  setDeleting]  = useState(false)
  const [confirmDel,setConfDel]   = useState(false)

  const ph = photos[idx]

  // Sync editable fields whenever photo index changes
  useEffect(() => {
    if (!ph) return
    setBodyArea(ph.body_area || '')
    setVisitDate(ph.visit_date || isoDate(new Date()))
    setPhotoNote(ph.notes || '')
    setConfDel(false)
  }, [idx]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard navigation
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowLeft')  setIdx(i => Math.max(0, i - 1))
      if (e.key === 'ArrowRight') setIdx(i => Math.min(photos.length - 1, i + 1))
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [photos.length]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    if (!ph) return
    setSaving(true)
    const { data, error } = await supabase.from('patient_photos')
      .update({ body_area: bodyArea, visit_date: visitDate, notes: photoNote })
      .eq('id', ph.id).select('*').single()
    if (!error && data) {
      onSaved(data)
      setSavedMsg(true)
      setTimeout(() => setSavedMsg(false), 2000)
    }
    setSaving(false)
  }

  async function handleDelete() {
    if (!ph) return
    setDeleting(true)
    const { error } = await supabase.from('patient_photos').delete().eq('id', ph.id)
    if (!error) {
      onDeleted(ph.id)
      if (photos.length <= 1) { onClose(); return }
      setIdx(i => Math.max(0, i - 1))
    }
    setDeleting(false); setConfDel(false)
  }

  if (!ph) return null

  // Dark-themed input style for the black overlay
  const dkSx = {
    width: '100%', padding: '7px 10px',
    border: '1px solid rgba(255,255,255,.18)', borderRadius: 7,
    fontSize: 12.5, color: T.white, background: 'rgba(255,255,255,.07)',
    outline: 'none', fontFamily: T.font, boxSizing: 'border-box',
  }
  const dkLabel = {
    fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.4)',
    textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 5,
  }

  const navBtn = (disabled) => ({
    width: 44, height: 44, borderRadius: 22, flexShrink: 0, margin: '0 6px',
    background: disabled ? 'rgba(255,255,255,.04)' : 'rgba(255,255,255,.13)',
    border: 'none', color: disabled ? 'rgba(255,255,255,.2)' : '#fff',
    fontSize: 22, cursor: disabled ? 'default' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background .12s',
  })

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)', zIndex: 500, display: 'flex', flexDirection: 'column', fontFamily: T.font }}>

      {/* ── Top bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: T.teal400, textTransform: 'uppercase', letterSpacing: '.9px' }}>
          {patientName}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isAdmin ? null : !confirmDel ? (
            <button onClick={() => setConfDel(true)} style={{ padding: '5px 12px', border: '1px solid rgba(239,68,68,.4)', borderRadius: 6, background: 'rgba(239,68,68,.1)', color: '#fca5a5', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>
              🗑 Delete Photo
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#fca5a5', fontWeight: 600 }}>Delete this photo?</span>
              <button onClick={() => setConfDel(false)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,.2)', background: 'transparent', color: 'rgba(255,255,255,.7)', fontSize: 11.5, cursor: 'pointer', fontFamily: T.font }}>No</button>
              <button onClick={handleDelete} disabled={deleting} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#dc2626', color: T.white, fontSize: 11.5, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: T.font }}>
                {deleting ? '…' : 'Yes, Delete'}
              </button>
            </div>
          )}
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,.12)', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
      </div>

      {/* ── Photo + nav arrows ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'hidden', minHeight: 0 }}>
        <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
          style={navBtn(idx === 0)}
          onMouseEnter={e => { if (idx > 0) e.currentTarget.style.background = 'rgba(255,255,255,.22)' }}
          onMouseLeave={e => { if (idx > 0) e.currentTarget.style.background = 'rgba(255,255,255,.13)' }}
        >‹</button>

        <div style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 0', overflow: 'hidden' }}>
          <PhotoImg photo_url={ph.photo_url} alt="skin" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8, boxShadow: '0 0 60px rgba(0,0,0,.6)' }} />
        </div>

        <button onClick={() => setIdx(i => Math.min(photos.length - 1, i + 1))} disabled={idx === photos.length - 1}
          style={navBtn(idx === photos.length - 1)}
          onMouseEnter={e => { if (idx < photos.length - 1) e.currentTarget.style.background = 'rgba(255,255,255,.22)' }}
          onMouseLeave={e => { if (idx < photos.length - 1) e.currentTarget.style.background = 'rgba(255,255,255,.13)' }}
        >›</button>
      </div>

      {/* ── Bottom metadata panel ── */}
      <div style={{ background: 'rgba(255,255,255,.05)', borderTop: '1px solid rgba(255,255,255,.08)', padding: '16px 24px 18px', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={dkLabel}>Body Area</label>
            <select value={bodyArea} onChange={e => setBodyArea(e.target.value)} style={{ ...dkSx, cursor: 'pointer' }}>
              <option value="" style={{ background: '#1e293b' }}>Select area…</option>
              {BODY_AREAS.map(a => <option key={a} value={a} style={{ background: '#1e293b' }}>{a}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={dkLabel}>Visit Date</label>
            <input type="date" value={visitDate} onChange={e => setVisitDate(e.target.value)} style={dkSx} />
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={dkLabel}>Clinical Note</label>
          <textarea value={photoNote} onChange={e => setPhotoNote(e.target.value)} placeholder="Add a clinical note…" rows={2}
            style={{ ...dkSx, resize: 'vertical', lineHeight: 1.5 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Counter */}
          <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,.4)' }}>{idx + 1} / {photos.length}</span>
          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid rgba(255,255,255,.2)', background: 'transparent', color: 'rgba(255,255,255,.75)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
            {can('photos.edit') && <button onClick={handleSave} disabled={saving}
              style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: savedMsg ? '#16a34a' : saving ? T.teal700 : T.teal500, color: T.white, fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer', fontFamily: T.font, transition: 'background .25s' }}>
              {saving ? 'Saving…' : savedMsg ? 'Saved ✓' : 'Save Changes'}
            </button>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Add Note modal ───────────────────────────────────────────────────────────
const NOTE_TYPES = ['Clinical Note', 'Follow-up', 'Observation', 'Lab Result']

function AddNoteModal({ appt, onClose, onSaved }) {
  const { userProfile } = useAuth()
  const [date,     setDate]     = useState(isoDate(new Date()))
  const [noteType, setNoteType] = useState('Clinical Note')
  const [noteText, setNoteText] = useState('')
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState('')

  async function handleSave() {
    if (!noteText.trim()) { setError('Please enter a note.'); return }
    setSaving(true)
    try {
      const { data, error } = await supabase.from('visit_notes').insert({
        appointment_id: appt.id,
        patient_id:     appt.patient_id,
        note_type:      noteType,
        note_text:      noteText.trim(),
        created_by:     userProfile?.full_name || appt.doctor || null,
        clinic_id:      userProfile?.clinic_id,
      }).select('*').single()
      if (error) throw error
      onSaved(data)
    } catch(e) { setError(friendlyError(e, 'Save failed')); setSaving(false) }
  }

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.65)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: T.font }}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.35)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', background: T.teal900, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.white }}>📝 Add Clinical Note</span>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(255,255,255,.14)', border: 'none', color: T.white, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={{ padding: 20 }}>
          {error && <div style={{ padding: '8px 12px', borderRadius: 7, background: '#fff1f2', color: '#dc2626', fontSize: 12.5, marginBottom: 12 }}>⚠️ {error}</div>}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <FLabel>Date</FLabel>
              <FInput type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <FLabel>Note Type</FLabel>
              <FSelect value={noteType} onChange={e => setNoteType(e.target.value)}>
                {NOTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </FSelect>
            </div>
          </div>
          <FLabel>Note</FLabel>
          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={5} placeholder="Enter clinical note…"
            style={{ ...sx, resize: 'vertical' }}
            onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
            onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }} />
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.gray100}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? T.teal200 : T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer', fontFamily: T.font }}>
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Prescription pad modal ────────────────────────────────────────────────────
const ADVICE_OPTIONS = [
  'Avoid sun exposure', 'Apply SPF 50+ sunscreen daily', 'Avoid spicy/oily food',
  'Keep area moisturised', 'Avoid scratching', 'Stay well hydrated',
  'Avoid alcohol & smoking', 'Gentle cleansing only',
]
function calcRxFollowUpDate(label) {
  const map = { '1 week': 7, '2 weeks': 14, '1 month': 30, '3 months': 90, '6 months': 180 }
  const days = map[label]
  if (!days) return ''
  const d = new Date(); d.setDate(d.getDate() + days); return isoDate(d)
}

function RxModal({ appt, patient, onClose, onSaved, prefill = {} }) {
  const { userProfile, clinic, doctors } = useAuth()
  const [chosenDoctor, setChosenDoctor] = useState(appt.doctor || '')
  const prescriber = resolvePrescriber(userProfile, doctors, chosenDoctor)
  const pd       = patient || appt.patients || {}
  const patName  = pd.full_name || 'Patient'
  const patPhone = (pd.phone || '').replace(/\D/g, '')
  const today    = new Date()

  const [chiefComplaint,      setChiefComplaint]      = useState(prefill?.chief_complaint      || '')
  const [examinationFindings, setExaminationFindings] = useState(prefill?.examination_findings || '')
  const [diagnosis,           setDiagnosis]           = useState(prefill?.diagnosis            || '')
  const [medicines, setMedicines] = useState(
    prefill?.medicines?.length > 0
      ? prefill.medicines
      : [{ name:'', formulation:'', dosage:'', duration:'', instructions:'' }]
  )
  const [advice,    setAdvice]    = useState(prefill?.advice     || [])
  const [followUp,  setFollowUp]  = useState(prefill?.follow_up  || '')
  const [followUpDate, setFollowUpDate] = useState(prefill?.followup_date || calcRxFollowUpDate(prefill?.follow_up || '') || '')
  const [additionalNotes, setAdditionalNotes] = useState(prefill?.additional_notes || '')
  const [saving,     setSaving]     = useState(false)
  const [savedToast, setSavedToast] = useState('')

  function handleFollowUpChange(val) {
    setFollowUp(val)
    if (val === 'as_needed' || val === '' || val === 'custom') setFollowUpDate('')
    else setFollowUpDate(calcRxFollowUpDate(val))
  }

  function fmtFollowUpLong(iso) {
    if (!iso) return ''
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  function sendFollowUpReminder() {
    if (!patPhone) { alert('No phone number on file for this patient.'); return }
    const dateStr = followUpDate ? fmtFollowUpLong(followUpDate) : followUp
    const msg = `Hello ${patName}, this is a reminder from ${clinic?.name || 'the clinic'}. Your follow-up appointment is scheduled for ${dateStr}. ${clinic?.phone ? `Please call us at ${clinic.phone} to confirm.` : 'Please call us to confirm.'} Thank you! 🌿`
    window.open(`https://wa.me/91${patPhone}?text=${encodeURIComponent(msg)}`, '_blank')
  }

  function sendEmailReminder() {
    const email = (pd.email || '').trim()
    if (!email) { alert('No email on record for this patient.'); return }
    const dateStr = followUpDate ? fmtFollowUpLong(followUpDate) : followUp
    const cName   = clinic?.name || 'the clinic'
    const subject = `Follow-up Reminder - ${cName}`
    const body = `Hello ${patName},\n\nThis is a reminder from ${cName}.\n\nYour follow-up appointment is scheduled for ${dateStr}.\n\nPlease call us${clinic?.phone ? ` at ${clinic.phone}` : ''} to confirm your appointment.\n\nThank you!\n${cName} 🌿`
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  function addMed()         { setMedicines(m => [...m, { name:'', formulation:'', dosage:'', duration:'', instructions:'' }]) }
  function setMed(i, k, v)  { setMedicines(m => m.map((r,j) => j===i ? { ...r, [k]: v } : r)) }
  function removeMed(i)     { setMedicines(m => m.filter((_,j) => j!==i)) }
  function toggleAdv(a)     { setAdvice(p => p.includes(a) ? p.filter(x=>x!==a) : [...p, a]) }

  function buildRxText() {
    return [
      chiefComplaint      && `Chief Complaint: ${chiefComplaint}`,
      examinationFindings && `Findings: ${examinationFindings}`,
      diagnosis           && `Diagnosis: ${diagnosis}`,
      medicines.filter(m=>m.name).length && 'Rx:\n' + medicines.filter(m=>m.name).map(m=>`  • ${m.name}${m.formulation?' ('+m.formulation+')':''} — ${m.dosage}${m.duration?' × '+m.duration:''}${m.instructions?' ['+m.instructions+']':''}`).join('\n'),
      advice.length && `Advice: ${advice.join(' · ')}`,
      followUp && (followUp === 'as_needed' ? 'Follow-up: As needed' : followUp === 'custom' ? (followUpDate ? `Follow-up: ${fmtFollowUpLong(followUpDate)}` : null) : `Follow-up: ${followUp}${followUpDate ? ` (${fmtFollowUpLong(followUpDate)})` : ''}`),
      additionalNotes && `Notes: ${additionalNotes}`,
    ].filter(Boolean).join('\n\n')
  }

  async function saveToVisit() {
    const rxText = buildRxText()
    if (!rxText.trim()) { alert('Please fill in at least one field.'); return }
    if (!prescriber) { alert('Please choose the prescribing doctor.'); return }
    setSaving(true)
    try {
      const { data, error } = await supabase.from('visit_notes').insert({
        appointment_id: appt.id,
        patient_id:     appt.patient_id,
        note_type:      'Prescription',
        note_text:      rxText,
        created_by:     prescriber.full_name,
        clinic_id:      userProfile?.clinic_id,
      }).select('*').single()
      if (error) throw error
      // Always save to prescriptions so Rx Pad can reload all fields later
      const { error: rxErr } = await supabase.from('prescriptions').insert({
        patient_id:           appt.patient_id,
        patient_name:         pd.full_name || '',
        doctor_name:          prescriber.full_name,
        clinic_id:            userProfile?.clinic_id,
        appointment_id:       appt.id,
        chief_complaint:      chiefComplaint      || null,
        examination_findings: examinationFindings || null,
        diagnosis:            diagnosis           || null,
        medicines: medicines.filter(m => m.name).map(m => ({
          name:         m.name         || '',
          formulation:  m.formulation  || '',
          dosage:       m.dosage       || '',
          duration:     m.duration     || '',
          instructions: m.instructions || '',
        })),
        advice:           advice || [],
        follow_up:        followUp === 'as_needed' ? 'As needed' : followUp === 'custom' ? 'Custom date' : followUp || null,
        followup_date:    followUpDate     || null,
        additional_notes: additionalNotes || null,
      })
      if (rxErr) throw rxErr
      // Notify parent to add note to Visit History — do NOT close the modal
      onSaved && onSaved(data)
      // Show success toast; form fields stay intact
      const msg = prefill.editingDate ? 'New prescription saved. Previous visit unchanged.' : 'Saved to Visit History'
      setSavedToast(msg)
      setTimeout(() => setSavedToast(''), 2500)
    } catch(e) { alert(friendlyError(e, 'Save failed')) }
    setSaving(false)
  }

  function rxOutput() {
    return {
      clinic, doctor: prescriber,
      patient: { name: patName, age: pd.age, gender: pd.gender },
      date: today, complaint: chiefComplaint, findings: examinationFindings, diagnosis,
      medicines, advice, notes: additionalNotes,
      followUp: followUp === 'as_needed' ? 'As needed'
        : followUp === 'custom' ? (followUpDate ? fmtFollowUpLong(followUpDate) : '')
        : [followUp, followUpDate && `(${fmtFollowUpLong(followUpDate)})`].filter(Boolean).join(' '),
    }
  }

  function sendWhatsApp() {
    if (!patPhone) { alert('No phone number on file.'); return }
    if (!prescriber) { alert('Please choose the prescribing doctor.'); return }
    window.open(`https://wa.me/91${patPhone}?text=${encodeURIComponent(buildRxWhatsApp(rxOutput()))}`, '_blank')
  }

  function printRx() {
    if (!prescriber) { alert('Please choose the prescribing doctor.'); return }
    printPrescription(rxOutput())
  }

  const tdInp = { ...sx, fontSize: 12, padding: '5px 7px' }
  const thSx2 = { fontSize: 10, fontWeight: 700, color: T.gray500, textTransform: 'uppercase' }

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.65)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: T.font }}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '94vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.35)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', background: T.teal900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.white }}>
              {prefill.editingDate ? `📋 Rx Pad — Editing visit from ${prefill.editingDate}` : '📋 Prescription Pad'}
            </div>
            <div style={{ fontSize: 11.5, color: T.teal200, marginTop: 2 }}>{patName} · {prescriber?.full_name || 'Doctor not selected'} · {today.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(255,255,255,.14)', border: 'none', color: T.white, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {/* Patient strip */}
          <div style={{ display: 'flex', gap: 16, padding: '10px 14px', background: T.teal50, borderRadius: 8, border: `1px solid ${T.teal100}`, marginBottom: 16 }}>
            {[['Patient', patName],['Age', pd.age?pd.age+' yrs':'—'],['Gender', pd.gender||'—']].map(([l,v]) => (
              <div key={l}><label style={{ fontSize: 9.5, fontWeight: 700, color: T.teal600, textTransform: 'uppercase', letterSpacing: '.5px', display: 'block' }}>{l}</label><div style={{ fontSize: 13, fontWeight: 600, color: T.gray900 }}>{v}</div></div>
            ))}
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 9.5, fontWeight: 700, color: T.teal600, textTransform: 'uppercase', letterSpacing: '.5px', display: 'block' }}>Prescribing Doctor</label>
              {userProfile?.registration_no ? (
                <div style={{ fontSize: 13, fontWeight: 600, color: T.gray900 }}>{userProfile.full_name}</div>
              ) : (
                <select value={prescriber?.full_name || ''} onChange={e => setChosenDoctor(e.target.value)}
                  style={{ ...sx, padding: '4px 8px', fontSize: 12.5, background: T.white }}>
                  <option value="">{doctors.length ? 'Select doctor…' : 'No registered doctors'}</option>
                  {doctors.map(d => <option key={d.id} value={d.full_name}>{d.full_name}</option>)}
                </select>
              )}
              {prescriber && <div style={{ fontSize: 10.5, color: T.gray500, marginTop: 2 }}>{[prescriber.qualification, regLine(prescriber)].filter(Boolean).join(' · ')}</div>}
            </div>
          </div>

          {/* Clinical fields */}
          <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
            {[['Chief Complaint', chiefComplaint, setChiefComplaint, 'Patient\'s main complaint…'],['Examination Findings', examinationFindings, setExaminationFindings, 'Examination findings…']].map(([label, val, setter, ph]) => (
              <div key={label}><FLabel>{label}</FLabel>
                <textarea value={val} onChange={e => setter(e.target.value)} rows={2} placeholder={ph} style={{ ...sx, resize: 'vertical' }}
                  onFocus={e => { e.target.style.borderColor=T.teal500; e.target.style.boxShadow=`0 0 0 3px ${T.teal100}` }}
                  onBlur={e =>  { e.target.style.borderColor=T.gray200;  e.target.style.boxShadow='none' }} />
              </div>
            ))}
            <div><FLabel>Diagnosis</FLabel><FInput value={diagnosis} onChange={e => setDiagnosis(e.target.value)} placeholder="Primary diagnosis…" /></div>
          </div>

          {/* Rx medicines */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: T.teal800, fontFamily: 'serif', lineHeight: 1 }}>℞</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '.5px' }}>Medicines</span>
              </div>
              <button onClick={addMed} style={{ fontSize: 11.5, color: T.teal600, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: T.font }}>+ Add Medicine</button>
            </div>
            <div style={{ border: `1px solid ${T.gray200}`, borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1.5fr 28px', background: T.gray50, padding: '6px 10px', gap: 6 }}>
                {['Medicine', 'Form', 'Dosage', 'Duration', 'Instructions', ''].map(h => <div key={h} style={thSx2}>{h}</div>)}
              </div>
              {medicines.map((m, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1.5fr 28px', padding: '5px 10px', gap: 6, borderTop: `1px solid ${T.gray100}`, alignItems: 'center' }}>
                  <input value={m.name}         onChange={e => setMed(i,'name',         e.target.value)} placeholder="e.g. Clindamycin" style={tdInp} />
                  <input value={m.formulation}  onChange={e => setMed(i,'formulation',  e.target.value)} placeholder="Gel/Tab"         style={tdInp} />
                  <input value={m.dosage}       onChange={e => setMed(i,'dosage',       e.target.value)} placeholder="OD / BD"         style={tdInp} />
                  <input value={m.duration}     onChange={e => setMed(i,'duration',     e.target.value)} placeholder="7 days"          style={tdInp} />
                  <input value={m.instructions} onChange={e => setMed(i,'instructions', e.target.value)} placeholder="After meals"     style={tdInp} />
                  <button onClick={() => removeMed(i)} disabled={medicines.length===1} style={{ background:'none', border:'none', color: medicines.length===1?T.gray200:'#dc2626', cursor: medicines.length===1?'default':'pointer', fontSize:16, fontFamily:T.font }}>×</button>
                </div>
              ))}
            </div>
          </div>

          {/* Advice */}
          <div style={{ marginBottom: 14 }}>
            <FLabel>Advice &amp; Lifestyle</FLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ADVICE_OPTIONS.map(a => {
                const on = advice.includes(a)
                return (
                  <button key={a} onClick={() => toggleAdv(a)} style={{ padding: '5px 11px', borderRadius: 20, fontSize: 11.5, fontWeight: on?700:500, border: `1.5px solid ${on?T.teal500:T.gray300}`, background: on?T.teal50:T.white, color: on?T.teal700:T.gray500, cursor: 'pointer', fontFamily: T.font, transition: 'all .12s' }}>
                    {on ? '✓ ' : ''}{a}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Follow-up + notes */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 4 }}>
            <div>
              <FLabel>Follow-up</FLabel>
              {/* Native select — avoids FSelect onChange event-object bug */}
              <select
                value={followUp}
                onChange={e => handleFollowUpChange(e.target.value)}
                style={{
                  width: '100%', padding: '9px 12px', border: `1.5px solid ${T.gray200}`,
                  borderRadius: 8, fontSize: 13.5, color: T.gray900, background: T.white,
                  outline: 'none', fontFamily: T.font, boxSizing: 'border-box', cursor: 'pointer',
                }}>
                <option value="">Select…</option>
                <option value="1 week">1 week</option>
                <option value="2 weeks">2 weeks</option>
                <option value="1 month">1 month</option>
                <option value="3 months">3 months</option>
                <option value="6 months">6 months</option>
                <option value="as_needed">As needed</option>
                <option value="custom">Custom date</option>
              </select>
              {followUp === 'custom' && (
                <input type="date" value={followUpDate}
                  min={isoDate(new Date())}
                  onChange={e => setFollowUpDate(e.target.value)}
                  style={{ ...sx, marginTop: 6, fontSize: 13 }} />
              )}
              {followUpDate && followUp !== 'as_needed' && (
                <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                  📅 {fmtFollowUpLong(followUpDate)}
                </div>
              )}
              {followUpDate && followUp !== 'as_needed' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
                  <button type="button" onClick={sendFollowUpReminder} style={{
                    padding: '5px 10px', borderRadius: 6, border: '1.5px solid #22c55e',
                    background: '#f0fdf4', color: '#15803d', fontSize: 11.5, fontWeight: 700,
                    cursor: 'pointer', fontFamily: T.font, textAlign: 'left',
                  }}>📱 WhatsApp Reminder</button>
                  <button type="button" onClick={sendEmailReminder} style={{
                    padding: '5px 10px', borderRadius: 6, border: `1.5px solid ${T.gray200}`,
                    background: T.white, color: T.teal700, fontSize: 11.5, fontWeight: 700,
                    cursor: 'pointer', fontFamily: T.font, textAlign: 'left',
                  }}>📧 Email Reminder</button>
                </div>
              )}
            </div>
            <div><FLabel>Additional Notes</FLabel><FInput value={additionalNotes} onChange={e => setAdditionalNotes(e.target.value)} placeholder="Any additional notes…" /></div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.gray100}`, display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, position: 'relative' }}>
          {/* Success toast */}
          {savedToast && (
            <div style={{
              position: 'absolute', top: -46, left: '50%', transform: 'translateX(-50%)',
              background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700,
              padding: '8px 20px', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.18)',
              whiteSpace: 'nowrap', pointerEvents: 'none',
            }}>
              ✓ {savedToast}
            </div>
          )}
          <button onClick={sendWhatsApp} style={{ padding: '7px 13px', borderRadius: 8, border: '1.5px solid #22c55e', background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>💬 WhatsApp Rx</button>
          <button onClick={printRx}      style={{ padding: '7px 13px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>🖨️ Print Rx</button>
          <button onClick={onClose}      style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
          <button onClick={saveToVisit} disabled={saving}
            style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? T.teal200 : savedToast ? '#16a34a' : T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer', fontFamily: T.font, transition: 'background .25s' }}>
            {saving ? 'Saving…' : savedToast ? '✓ Saved' : 'Save to Visit History'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROFILE PANEL — slides in from the right (prototype-exact)
// ═══════════════════════════════════════════════════════════════════════════════
function ProfilePanel({ appt, onClose, onUpdated, onDeleted, onEdit }) {
  const { clinic, can, isAdmin, role } = useAuth()
  const clinical = can('clinical.view')  // doctor notes, visit notes, prescriptions, conditions
  const [status, setStatus]         = useState(appt.status)
  const [notes, setNotes]           = useState(appt.notes || '')
  const [notesDirty, setNotesDirty] = useState(false)
  const [saving, setSaving]         = useState(false)
  const [savedMsg, setSavedMsg]     = useState(false)
  const [deleting, setDeleting]     = useState(false)
  const [confirmDel, setConfDel]    = useState(false)
  const [patient, setPatient]       = useState(null)
  const [visits, setVisits]         = useState([])
  const [invoices, setInvoices]     = useState([])
  const [expanded, setExpanded]     = useState(null)
  const [photos, setPhotos]             = useState([])
  const [lightboxIdx, setLightboxIdx]   = useState(null)
  const [photoUploadModal, setPhotoUM]  = useState(false)
  const [addNoteModal, setAddNoteM]     = useState(false)
  const [invoiceModal, setInvModal]     = useState(false)
  const [rxModal, setRxModal]           = useState(null)
  const [newNote, setNewNote]           = useState('')
  const [visitNotes, setVisitNotes]     = useState([])
  const [editingPatient, setEditingPt]  = useState(false)
  const [patientEdits, setPatEdits]     = useState({})
  const [savingPt, setSavingPt]         = useState(false)

  const pd = appt.patients || {}
  const initials = avatarInitials(pd.full_name)

  useEffect(() => {
    if (!appt.patient_id) return
    supabase.from('patients').select('*').eq('id', appt.patient_id).single().then(({ data }) => { if (data) setPatient(data) })
    supabase.from('appointments').select('*').eq('patient_id', appt.patient_id).order('date', { ascending: false }).limit(10)
      .then(({ data }) => setVisits((data || []).filter(v => v.id !== appt.id)))
    supabase.from('invoices').select('*').eq('patient_id', appt.patient_id).order('created_at', { ascending: false }).limit(6)
      .then(({ data }) => setInvoices(data || []))
    supabase.from('patient_photos').select('*').eq('patient_id', appt.patient_id).order('visit_date', { ascending: false })
      .then(({ data }) => setPhotos(data || []))
    if (clinical) {
      supabase.from('visit_notes').select('*').eq('patient_id', appt.patient_id).order('created_at', { ascending: false }).limit(30)
        .then(({ data }) => setVisitNotes(data || []))
    }
  }, [appt.patient_id, appt.id, clinical])

  async function updateStatus(s) {
    setSaving(true)
    const { data, error } = await supabase.from('appointments').update({ status: s, notes }).eq('id', appt.id).select('*, patients(full_name, phone)').single()
    if (!error) { setStatus(s); onUpdated({ ...appt, ...data, status: s }) }
    setSaving(false)
  }

  async function saveNotes() {
    const text = newNote.trim()
    if (!text) return
    setSaving(true)
    const now = new Date()
    const ts = now.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
            + ', ' + now.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })
    const entry    = `${ts}: ${text}`
    const combined = notes ? `${notes}\n${entry}` : entry
    const { data } = await supabase.from('appointments').update({ notes: combined }).eq('id', appt.id).select('*, patients(full_name, phone)').single()
    if (data) {
      setNotes(combined)
      setNewNote('')
      onUpdated({ ...appt, ...data, notes: combined })
      setSavedMsg(true)
      setTimeout(() => setSavedMsg(false), 2500)
    }
    setSaving(false); setNotesDirty(false)
  }

  async function savePatientDetails() {
    setSavingPt(true)
    const allUpdates = {
      age:           patientEdits.age ? parseInt(patientEdits.age, 10) : null,
      gender:        patientEdits.gender        || null,
      skin_type:     patientEdits.skin_type     || null,
      blood_group:   patientEdits.blood_group   || null,
      email:         patientEdits.email         || null,
      allergies:     patientEdits.allergies     || null,
      date_of_birth: patientEdits.date_of_birth || null,
    }
    // Try full set first; if a column is missing, retry with a smaller set
    const fieldSets = [
      ['age','gender','skin_type','blood_group','email','allergies','date_of_birth'],
      ['age','gender','skin_type','blood_group','email','allergies'],
      ['age','gender','skin_type','blood_group','email'],
      ['age','gender','skin_type','blood_group'],
      ['age','gender'],
    ]
    for (const fields of fieldSets) {
      const payload = {}
      fields.forEach(f => { payload[f] = allUpdates[f] })
      const { data, error } = await supabase.from('patients').update(payload).eq('id', patient.id).select('*').single()
      if (!error && data) { setPatient(data); setEditingPt(false); setSavingPt(false); return }
      const isColErr = error?.message?.toLowerCase().includes('column') || error?.code === '42703'
      if (!isColErr) { alert('Save failed: ' + (error?.message || 'Unknown error')); setSavingPt(false); return }
    }
    alert('Could not save — please run ALTER TABLE patients ADD COLUMN allergies text; in Supabase SQL Editor.')
    setSavingPt(false)
  }

  async function handleDelete() {
    setDeleting(true)
    const { error } = await supabase.from('appointments').delete().eq('id', appt.id)
    if (!error) onDeleted(appt.id)
    else { setDeleting(false); alert(friendlyError(error, 'Delete failed')) }
  }

  function sendWhatsApp() {
    const phone = pd.phone?.replace(/\D/g, '')
    if (!phone) return alert('No phone number on file for this patient.')
    const msg = `Hello ${pd.full_name}, this is a reminder from ${clinic?.name || 'the clinic'}. Your ${appt.service_type} appointment is on ${fmtDate(appt.date)} at ${fmtTime(appt.time)}. Please arrive 5 minutes early.`
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`, '_blank')
  }

  async function openRxPadWithData(item) {
    const noteDate = item.created_at?.split('T')[0]
    const editingDate = item.created_at
      ? new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      : null

    // Try to find a matching prescription in the prescriptions table (same patient, same day)
    let prefill = { editingDate }
    if (noteDate && item.patient_id) {
      const { data: rx } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('patient_id', item.patient_id)
        .gte('created_at', noteDate + 'T00:00:00')
        .lte('created_at', noteDate + 'T23:59:59')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (rx) {
        prefill = {
          editingDate,
          chief_complaint:      rx.chief_complaint      || rx.complaint || '',
          examination_findings: rx.examination_findings || rx.findings  || '',
          diagnosis:            rx.diagnosis            || '',
          medicines: (rx.medicines || []).map(m => ({
            name:         m.name         || '',
            formulation:  m.formulation  || m.form  || '',
            dosage:       m.dosage       || m.dose  || '',
            duration:     m.duration     || m.dur   || '',
            instructions: m.instructions || m.instr || '',
          })),
          advice: Array.isArray(rx.advice) ? rx.advice
                  : typeof rx.advice === 'string' ? (() => { try { return JSON.parse(rx.advice) } catch { return [] } })()
                  : [],
          follow_up:        rx.follow_up        || '',
          followup_date:    rx.followup_date     || '',
          additional_notes: rx.additional_notes || rx.notes || '',
        }
      } else {
        // Fallback: parse formatted note_text
        const text = item.note_text || ''
        const getField = key => { const m = text.match(new RegExp(key + ': (.+)')); return m?.[1]?.trim() || '' }
        prefill = {
          editingDate,
          chief_complaint:      getField('Chief Complaint'),
          examination_findings: getField('Findings'),
          diagnosis:            getField('Diagnosis'),
          medicines:            [],
          advice:               [],
          follow_up:            getField('Follow-up'),
          additional_notes:     getField('Notes'),
        }
      }
    }
    setRxModal(prefill)
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,.45)', zIndex: 299, cursor: 'pointer', transition: 'opacity .28s' }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, maxWidth: '100vw',
        background: T.white, zIndex: 300,
        boxShadow: '-10px 0 48px rgba(0,0,0,.18)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        fontFamily: T.font,
        animation: 'ppSlideIn .3s cubic-bezier(.4,0,.2,1)',
      }}>
        <style>{`@keyframes ppSlideIn { from { transform: translateX(100%) } to { transform: translateX(0) } }`}</style>

        {/* ── Header ── */}
        <div style={{
          background: `linear-gradient(145deg, ${T.teal950} 0%, ${T.teal800} 100%)`,
          padding: '18px 18px 14px', flexShrink: 0, position: 'relative',
        }}>
          <button onClick={onClose} style={{
            position: 'absolute', top: 13, right: 13, width: 28, height: 28, border: 'none',
            background: 'rgba(255,255,255,.14)', borderRadius: 7,
            color: 'rgba(255,255,255,.85)', fontSize: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13 }}>
            <div style={{
              width: 54, height: 54, borderRadius: 14, flexShrink: 0,
              background: 'rgba(255,255,255,.18)', border: '2px solid rgba(255,255,255,.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 21, fontWeight: 800, color: T.white, letterSpacing: -0.5,
            }}>{initials}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: T.white, lineHeight: 1.2 }}>{pd.full_name || '—'}</div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.7)', marginTop: 3 }}>
                {[patient?.age ? patient.age + ' yrs' : null, patient?.gender || pd.gender, patient?.blood_group].filter(Boolean).join(' · ') || (appt.service_type + ' · ' + fmtDate(appt.date))}
              </div>
              {(pd.phone || patient?.phone) && (
                <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>
                  {pd.phone || patient?.phone}{patient?.email ? '  ·  ' + patient.email : ''}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Scrollable Body ── */}
        <div style={{ flex: 1, overflowY: 'auto', background: T.gray50, padding: '10px 0' }}>

          {/* ── TODAY'S APPOINTMENT ── */}
          <div style={{ background: T.white, margin: '0 12px 10px', borderRadius: 10, border: `1px solid ${T.gray200}`, overflow: 'hidden' }}>
            <div style={{ padding: '10px 15px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: T.teal700, borderBottom: `1px solid ${T.gray100}`, display: 'flex', alignItems: 'center', gap: 6 }}>
              📅 Today's Appointment
            </div>

            {/* Info strip — teal50 left-border */}
            <div style={{ padding: '12px 15px', display: 'flex', flexDirection: 'column', gap: 9, background: T.teal50, borderLeft: `3px solid ${T.teal500}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.gray900 }}>{fmtDate(appt.date)} &nbsp;·&nbsp; {fmtTime(appt.time)}</div>
                <div style={{ fontSize: 11.5, color: T.gray500, marginTop: 3 }}>{appt.service_type} &nbsp;·&nbsp; {appt.doctor || 'Doctor'}</div>
              </div>
              {/* Status chips — prototype .sc-group style */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ALL_STATUSES.map(s => {
                  const c = STATUS[s]
                  const active = status === s
                  return (
                    <button key={s} onClick={() => !active && updateStatus(s)} disabled={saving || active}
                      style={{
                        padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${c.border}`,
                        background: active ? c.bg : T.white, color: active ? c.color : T.gray500,
                        fontSize: 11.5, fontWeight: 600, cursor: active ? 'default' : 'pointer',
                        opacity: active ? 1 : 0.45, fontFamily: T.font, transition: 'opacity .12s',
                      }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.opacity = '0.85' }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.opacity = '0.45' }}
                    >{c.label}</button>
                  )
                })}
              </div>
              {/* Complaint */}
              {clinical && appt.notes && !notesDirty && (
                <div style={{ fontSize: 12, color: T.gray600, borderTop: '1px solid rgba(0,0,0,.06)', paddingTop: 8, lineHeight: 1.55 }}>
                  <strong>Notes:</strong> {appt.notes}
                </div>
              )}
            </div>

            {/* Doctor Notes — history + new entry (clinical roles only) */}
            {clinical && <div style={{ padding: '10px 15px 8px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, marginBottom: 6 }}>Doctor Notes</div>
              {/* History */}
              {notes && (
                <div style={{ marginBottom: 8, borderRadius: 7, border: `1px solid ${T.gray200}`, background: T.gray50, maxHeight: 140, overflowY: 'auto', padding: '8px 10px' }}>
                  {notes.split('\n').filter(Boolean).map((line, i) => {
                    const colonIdx = line.indexOf(': ')
                    const hasTs = colonIdx > 4 && colonIdx < 36
                    return (
                      <div key={i} style={{ fontSize: 12, color: T.gray700, lineHeight: 1.55, marginBottom: 5, paddingBottom: 5, borderBottom: i < notes.split('\n').filter(Boolean).length - 1 ? `1px solid ${T.gray100}` : 'none' }}>
                        {hasTs ? (
                          <><span style={{ fontSize: 10.5, fontWeight: 700, color: T.teal600 }}>{line.slice(0, colonIdx)}</span>{': ' + line.slice(colonIdx + 2)}</>
                        ) : line}
                      </div>
                    )
                  })}
                </div>
              )}
              {/* New entry */}
              <textarea
                value={newNote}
                onChange={e => { setNewNote(e.target.value); setNotesDirty(true) }}
                placeholder="Add new note (saves with timestamp)…"
                rows={2}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1.5px solid ${T.gray200}`, fontSize: 12.5, fontFamily: T.font, color: T.gray700, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = T.teal500}
                onBlur={e =>  e.target.style.borderColor = T.gray200}
              />
            </div>}

            {/* Edit + Save Notes row */}
            <div style={{ padding: '0 15px 8px', display: 'flex', justifyContent: 'flex-end', gap: 7, alignItems: 'center' }}>
              <button onClick={() => onEdit(appt)} style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${T.teal200}`, background: T.teal50, color: T.teal700, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
                ✏️ Edit Appointment
              </button>
              {clinical && <button onClick={saveNotes} disabled={saving} style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: savedMsg ? '#16a34a' : T.teal600, color: T.white, fontSize: 12.5, fontWeight: 700, cursor: saving ? 'default' : 'pointer', fontFamily: T.font, transition: 'background .25s' }}>
                {saving ? 'Saving…' : savedMsg ? 'Saved ✓' : 'Save Notes'}
              </button>}
            </div>
            {/* Delete appointment — admin only (others change status to Cancelled) */}
            {isAdmin && <div style={{ padding: '0 15px 12px' }}>
              {!confirmDel ? (
                <button onClick={() => setConfDel(true)} style={{ background: 'none', border: 'none', color: T.gray400, fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: T.font }}>
                  Cancel this appointment
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 11.5, color: T.red600, fontWeight: 600 }}>Delete appointment?</span>
                  <button onClick={() => setConfDel(false)} style={{ padding: '4px 9px', borderRadius: 6, border: `1px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 11, cursor: 'pointer', fontFamily: T.font }}>No</button>
                  <button onClick={handleDelete} disabled={deleting} style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: T.red600, color: T.white, fontSize: 11, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: T.font }}>
                    {deleting ? '…' : 'Yes, Delete'}
                  </button>
                </div>
              )}
            </div>}
          </div>

          {/* ── PATIENT DETAILS ── */}
          {patient && (() => {
            // Compute first visit and total visits from appointment history
            const allDates = [...visits.map(v => v.date), appt.date].filter(Boolean).sort()
            const firstVisitDate = allDates.length ? fmtDate(allDates[0]) : '—'
            const totalVisits = visits.length + 1

            // Skin conditions: try patient.conditions (JSONB array), else parse medical_history
            const rawConds = patient.conditions
            const conditions = Array.isArray(rawConds) && rawConds.length > 0
              ? rawConds
              : typeof rawConds === 'string' && rawConds
                ? rawConds.split(/[,;]+/).map(s => s.trim()).filter(Boolean)
                : []

            return (
              <>
                {/* Patient Details card */}
                <div style={{ background: T.white, margin: '0 12px 10px', borderRadius: 10, border: `1px solid ${T.gray200}`, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 15px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: T.teal700, borderBottom: `1px solid ${T.gray100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>👤 Patient Details</span>
                    {!editingPatient && (
                      <button
                        onClick={() => {
                          setPatEdits({ age: patient.age || '', gender: patient.gender || '', skin_type: patient.skin_type || '', blood_group: patient.blood_group || '', email: patient.email || '', allergies: patient.allergies || '', date_of_birth: patient.date_of_birth || '' })
                          setEditingPt(true)
                        }}
                        style={{ padding: '3px 10px', borderRadius: 6, background: T.teal50, color: T.teal700, border: `1.5px solid ${T.teal200}`, fontSize: 10.5, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>
                        ✏️ Edit
                      </button>
                    )}
                  </div>
                  {/* Avatar row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 15px', background: T.teal50, borderBottom: `1px solid ${T.gray100}`, borderLeft: `3px solid ${T.teal400}` }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: `linear-gradient(135deg, ${T.teal500}, ${T.teal800})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, color: T.white, flexShrink: 0 }}>
                      {initials}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: T.gray900 }}>{patient.full_name}</div>
                      <div style={{ fontSize: 12, color: T.gray500 }}>{[patient.gender, patient.age ? patient.age + ' yrs' : null].filter(Boolean).join(', ')}</div>
                      {patient.phone && <div style={{ fontSize: 10.5, color: T.gray400, marginTop: 1 }}>{patient.phone}</div>}
                    </div>
                  </div>

                  {editingPatient ? (
                    /* ── Edit mode ── */
                    <div style={{ padding: '14px 15px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px', marginBottom: 12 }}>
                        {/* Age */}
                        <div>
                          <label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 4 }}>Age</label>
                          <input type="number" min="0" max="120" value={patientEdits.age}
                            onChange={e => setPatEdits(p => ({ ...p, age: e.target.value }))}
                            style={{ ...sx, padding: '7px 10px', fontSize: 13 }} />
                        </div>
                        {/* Gender */}
                        <div>
                          <label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 4 }}>Gender</label>
                          <select value={patientEdits.gender} onChange={e => setPatEdits(p => ({ ...p, gender: e.target.value }))}
                            style={{ ...sx, padding: '7px 10px', fontSize: 13, cursor: 'pointer' }}>
                            <option value="">Select…</option>
                            {['Male', 'Female', 'Other'].map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        {/* Skin Type */}
                        <div>
                          <label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 4 }}>Skin Type</label>
                          <select value={patientEdits.skin_type} onChange={e => setPatEdits(p => ({ ...p, skin_type: e.target.value }))}
                            style={{ ...sx, padding: '7px 10px', fontSize: 13, cursor: 'pointer' }}>
                            <option value="">Select…</option>
                            {['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        {/* Blood Group */}
                        <div>
                          <label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 4 }}>Blood Group</label>
                          <select value={patientEdits.blood_group} onChange={e => setPatEdits(p => ({ ...p, blood_group: e.target.value }))}
                            style={{ ...sx, padding: '7px 10px', fontSize: 13, cursor: 'pointer' }}>
                            <option value="">Select…</option>
                            {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                        {/* Email — full width */}
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 4 }}>Email</label>
                          <input type="email" value={patientEdits.email} onChange={e => setPatEdits(p => ({ ...p, email: e.target.value }))}
                            placeholder="patient@email.com"
                            style={{ ...sx, padding: '7px 10px', fontSize: 13 }} />
                        </div>
                        {/* Allergies — full width */}
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 4 }}>Allergies</label>
                          <input type="text" value={patientEdits.allergies} onChange={e => setPatEdits(p => ({ ...p, allergies: e.target.value }))}
                            placeholder="e.g. Penicillin, Latex"
                            style={{ ...sx, padding: '7px 10px', fontSize: 13 }} />
                        </div>
                        {/* Date of Birth */}
                        <div>
                          <label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 4 }}>Date of Birth</label>
                          <input type="date" value={patientEdits.date_of_birth || ''} onChange={e => setPatEdits(p => ({ ...p, date_of_birth: e.target.value }))}
                            style={{ ...sx, padding: '7px 10px', fontSize: 13, cursor: 'pointer' }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => setEditingPt(false)}
                          style={{ padding: '7px 16px', borderRadius: 7, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>
                          Cancel
                        </button>
                        <button disabled={savingPt} onClick={savePatientDetails}
                          style={{ padding: '7px 20px', borderRadius: 7, border: 'none', background: savingPt ? T.teal200 : T.teal600, color: T.white, fontSize: 12, fontWeight: 700, cursor: savingPt ? 'default' : 'pointer', fontFamily: T.font }}>
                          {savingPt ? 'Saving…' : 'Save Details'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Display mode ── */
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                      {[
                        ['Skin Type',    patient.skin_type   || '—'],
                        ['Blood Group',  patient.blood_group || '—'],
                        ['DOB',          patient.date_of_birth ? new Date(patient.date_of_birth + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'],
                        ['Email',        patient.email       || '—'],
                        ['Allergies',    patient.allergies   || 'None known'],
                        ['First Visit',  firstVisitDate],
                        ['Total Visits', totalVisits + (totalVisits === 1 ? ' visit' : ' visits')],
                      ].map(([label, val]) => (
                        <div key={label} style={{ padding: '9px 15px', borderBottom: `1px solid ${T.gray100}` }}>
                          <label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 2 }}>{label}</label>
                          <span style={{ fontSize: 13, fontWeight: 500, color: T.gray900 }}>{val}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── SKIN CONDITIONS & TREATMENTS ── */}
                {clinical && conditions.length > 0 && (
                  <div style={{ background: T.white, margin: '0 12px 10px', borderRadius: 10, border: `1px solid ${T.gray200}`, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 15px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: T.teal700, borderBottom: `1px solid ${T.gray100}` }}>
                      🏷 Skin Conditions &amp; Treatments
                    </div>
                    <div style={{ padding: '12px 15px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {conditions.map(c => {
                        const col = COND_COLORS[c] || { c: '#64748b', bg: '#f1f5f9' }
                        return (
                          <span key={c} style={{ padding: '4px 11px', borderRadius: 20, fontSize: 11.5, fontWeight: 600, border: `1.5px solid ${col.c}40`, color: col.c, background: col.bg }}>
                            {c}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* ── SKIN PHOTO HISTORY ── */}
                <div style={{ background: T.white, margin: '0 12px 10px', borderRadius: 10, border: `1px solid ${T.gray200}`, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 15px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: T.teal700, borderBottom: `1px solid ${T.gray100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>📷 Skin Photos ({photos.length})</span>
                    {can('photos.upload') && <button onClick={() => setPhotoUM(true)}
                      style={{ padding: '4px 11px', borderRadius: 6, background: T.teal50, color: T.teal700, border: `1.5px solid ${T.teal200}`, fontSize: 10.5, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>
                      + Add Photo
                    </button>}
                  </div>
                  {photos.length === 0 ? (
                    <div style={{ padding: 22, textAlign: 'center', color: T.gray400, fontSize: 13 }}>
                      No photos yet · Click <strong>+ Add Photo</strong> to track skin progress
                    </div>
                  ) : (
                    <div style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      {photos.map((ph, i) => (
                        <div key={ph.id} onClick={() => setLightboxIdx(i)}
                          style={{ cursor: 'pointer', borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.gray200}`, transition: 'box-shadow .12s' }}
                          onMouseEnter={e => e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,.14)'}
                          onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                        >
                          <PhotoImg photo_url={ph.photo_url} alt="skin" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                          <div style={{ padding: '4px 6px', fontSize: 9.5, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '.4px', textAlign: 'center', background: T.gray50 }}>
                            {ph.body_area || '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )
          })()}

          {/* Visit History Timeline — shows past appointments + clinical notes/prescriptions */}
          {(() => {
            // Merge appointments and visit_notes into one timeline, sorted newest first
            const apptItems = visits.map(v => ({ _type: 'appt', _sortKey: v.date + 'T' + (v.time || '00:00'), ...v }))
            const noteItems = visitNotes.map(n => ({ _type: 'note', _sortKey: n.created_at || '', ...n }))
            const timeline  = [...apptItems, ...noteItems].sort((a, b) => b._sortKey.localeCompare(a._sortKey)).slice(0, 12)
            const total     = visits.length + visitNotes.length

            return (
              <div style={{ background: T.white, margin: '0 12px 10px', borderRadius: 10, border: `1px solid ${T.gray200}`, overflow: 'hidden' }}>
                <div style={{ padding: '10px 15px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: T.teal700, borderBottom: `1px solid ${T.gray100}` }}>
                  🕐 Visit History ({total})
                </div>
                {total === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: T.gray400, fontSize: 13 }}>No previous visits or notes</div>
                ) : (
                  <div style={{ padding: '6px 0 4px' }}>
                    {timeline.map(item => {
                      const key = item._type + '-' + item.id
                      const isExpandedItem = expanded === key

                      if (item._type === 'appt') {
                        const sc = getStatus(item.status)
                        return (
                          <div key={key} style={{ padding: '0 12px', position: 'relative', marginBottom: 4 }}>
                            <div style={{ position: 'absolute', left: 21, top: 15, width: 11, height: 11, borderRadius: '50%', background: T.teal500, border: `2px solid ${T.white}`, boxShadow: `0 0 0 2px ${T.teal400}`, zIndex: 1 }} />
                            <div style={{ marginLeft: 24, border: `1px solid ${T.gray200}`, borderRadius: 8, background: T.white, overflow: 'hidden' }}>
                              <div onClick={() => setExpanded(isExpandedItem ? null : key)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer', userSelect: 'none' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: T.gray900 }}>{fmtDate(item.date)}</div>
                                  <div style={{ fontSize: 11, color: T.gray500, marginTop: 1 }}>{item.service_type}</div>
                                </div>
                                <span style={{ padding: '2px 9px', borderRadius: 10, fontSize: 10.5, fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.label}</span>
                                <span style={{ fontSize: 10, color: T.gray400, transition: 'transform .2s', transform: isExpandedItem ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
                              </div>
                              {isExpandedItem && (
                                <div style={{ borderTop: `1px solid ${T.gray100}`, padding: '2px 12px 10px' }}>
                                  {item.doctor && <div style={{ marginTop: 9 }}><label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 3 }}>Doctor</label><p style={{ fontSize: 12.5, color: T.gray700 }}>{item.doctor}</p></div>}
                                  {clinical && item.notes && <div style={{ marginTop: 9 }}><label style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: T.gray400, display: 'block', marginBottom: 3 }}>Notes</label><p style={{ fontSize: 12.5, color: T.gray700, lineHeight: 1.55 }}>{item.notes}</p></div>}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      }

                      // Clinical note / prescription entry
                      const noteColor = item.note_type === 'Prescription' ? { dot: '#7c3aed', bg: '#f5f3ff', color: '#6d28d9', border: '#c4b5fd' }
                        : item.note_type === 'Lab Result'  ? { dot: '#0369a1', bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' }
                        : { dot: T.teal600, bg: T.teal50, color: T.teal700, border: T.teal200 }
                      const noteIcon = item.note_type === 'Prescription' ? '📋' : item.note_type === 'Lab Result' ? '🔬' : '📝'
                      const noteDate = item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

                      const isPrescription = item.note_type === 'Prescription'
                      // Extract first line of chief complaint from note_text for preview
                      const ccPreview = isPrescription && item.note_text
                        ? (item.note_text.match(/Chief Complaint: (.+)/) || [])[1]?.trim() || ''
                        : ''
                      return (
                        <div key={key} style={{ padding: '0 12px', position: 'relative', marginBottom: 4 }}>
                          <div style={{ position: 'absolute', left: 21, top: 15, width: 11, height: 11, borderRadius: '50%', background: noteColor.dot, border: `2px solid ${T.white}`, boxShadow: `0 0 0 2px ${noteColor.dot}66`, zIndex: 1 }} />
                          <div style={{ marginLeft: 24, border: `1.5px solid ${noteColor.border}`, borderRadius: 8, background: noteColor.bg, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', userSelect: 'none' }}>
                              <span style={{ fontSize: 14, lineHeight: 1 }}>{noteIcon}</span>
                              <div onClick={() => setExpanded(isExpandedItem ? null : key)} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: noteColor.color }}>{item.note_type}</div>
                                <div style={{ fontSize: 11, color: T.gray500, marginTop: 1 }}>
                                  {noteDate}{item.created_by ? ' · ' + item.created_by : ''}
                                  {ccPreview && <span style={{ color: T.gray400 }}> · {ccPreview}</span>}
                                </div>
                              </div>
                              {isPrescription && can('prescriptions.write') && (
                                <button
                                  onClick={e => { e.stopPropagation(); openRxPadWithData(item) }}
                                  style={{ fontSize: 11, padding: '2px 8px', background: '#0d3d3d', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                  ✏️ Open in Rx Pad
                                </button>
                              )}
                              <span onClick={() => setExpanded(isExpandedItem ? null : key)} style={{ fontSize: 10, color: T.gray400, transition: 'transform .2s', transform: isExpandedItem ? 'rotate(90deg)' : 'none', display: 'inline-block', cursor: 'pointer' }}>▶</span>
                            </div>
                            {isExpandedItem && item.note_text && (
                              <div style={{ borderTop: `1px solid ${noteColor.border}`, padding: '8px 12px 10px' }}>
                                <pre style={{ fontSize: 12, color: T.gray700, lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0, fontFamily: T.font }}>{item.note_text}</pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })()}

          {/* Invoice History — always shown */}
          <div style={{ background: T.white, margin: '0 12px 10px', borderRadius: 10, border: `1px solid ${T.gray200}`, overflow: 'hidden' }}>
            <div style={{ padding: '10px 15px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: T.teal700, borderBottom: `1px solid ${T.gray100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>🧾 Invoice History ({invoices.length})</span>
              {can('invoices.create') && <button onClick={() => setInvModal(true)} style={{ padding: '4px 11px', borderRadius: 6, background: T.teal50, color: T.teal700, border: `1.5px solid ${T.teal200}`, fontSize: 10.5, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>
                Generate Invoice
              </button>}
            </div>
            {role === 'receptionist' && (
              <div style={{ padding: '6px 15px 0', fontSize: 11, color: T.gray400 }}>Showing today's invoices and pending balances.</div>
            )}
            {invoices.length === 0 ? (
              <div style={{ padding: '18px 20px', textAlign: 'center', color: T.gray400, fontSize: 13 }}>{role === 'receptionist' ? 'No invoices from today or with a balance due' : 'No invoices yet for this patient'}</div>
            ) : (
              invoices.map(inv => (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 15px', borderBottom: `1px solid ${T.gray100}`, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.teal50}
                  onMouseLeave={e => e.currentTarget.style.background = T.white}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: T.teal700, minWidth: 100 }}>{inv.invoice_number || inv.id?.slice(0, 8)}</span>
                  <span style={{ fontSize: 11.5, color: T.gray500, flex: 1 }}>{fmtInvDate(invDate(inv))}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.gray900 }}>{fmtINR(inv.total)}</span>
                  {(() => {
                    const st = normStatus(inv)
                    const bg = st === 'paid' ? '#dcfce7' : st === 'partial' ? '#fef3c7' : '#fee2e2'
                    const cl = st === 'paid' ? '#16a34a' : st === 'partial' ? '#d97706' : T.red600
                    return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', background: bg, color: cl }}>{st}</span>
                  })()}
                </div>
              ))
            )}
          </div>

        </div>{/* end pp-body */}

        {/* ── Quick Actions Footer — prototype: Book Next · Add Note · Invoice · WhatsApp ── */}
        <div style={{ background: T.white, borderTop: `1px solid ${T.gray200}`, padding: '10px 12px', display: 'flex', gap: 6, flexShrink: 0 }}>
          {[
            { icon: '📅', label: 'Book Next', action: () => { onClose() } },
            can('notes.write')         && { icon: '📝', label: 'Add Note',  action: () => setAddNoteM(true) },
            can('prescriptions.write') && { icon: '📋', label: 'Rx Pad',    action: () => setRxModal({}) },
            can('invoices.create')     && { icon: '🧾', label: 'Invoice',   action: () => setInvModal(true) },
            { icon: '💬', label: 'WhatsApp',  action: sendWhatsApp },
          ].filter(Boolean).map(btn => (
            <button key={btn.label} onClick={btn.action} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
              padding: '9px 4px', border: `1.5px solid ${T.gray200}`, borderRadius: 9, background: T.white,
              cursor: 'pointer', transition: 'all .14s', fontFamily: T.font,
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.teal400; e.currentTarget.style.background = T.teal50 }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.background = T.white }}>
              <span style={{ fontSize: 19, lineHeight: 1 }}>{btn.icon}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: T.gray500 }}>{btn.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Photo upload modal ── */}
      {photoUploadModal && appt.patient_id && (
        <PhotoUploadModal
          patientId={appt.patient_id}
          onClose={() => setPhotoUM(false)}
          onSaved={ph => { setPhotos(p => [ph, ...p]); setPhotoUM(false) }}
        />
      )}

      {/* ── Photo lightbox ── */}
      {lightboxIdx !== null && photos.length > 0 && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIdx}
          patientName={pd.full_name || 'Patient'}
          onClose={() => setLightboxIdx(null)}
          onSaved={updated => setPhotos(p => p.map(ph => ph.id === updated.id ? updated : ph))}
          onDeleted={id => {
            const next = photos.filter(ph => ph.id !== id)
            setPhotos(next)
            if (next.length === 0) setLightboxIdx(null)
          }}
        />
      )}

      {/* ── Add Note modal ── */}
      {addNoteModal && (
        <AddNoteModal
          appt={appt}
          onClose={() => setAddNoteM(false)}
          onSaved={note => {
            setVisitNotes(n => [note, ...n])
            setAddNoteM(false)
          }}
        />
      )}

      {/* ── Invoice modal ── */}
      {invoiceModal && (
        <InvoiceModal
          appointmentId={appt.id}
          patient={{ id: appt.patient_id, ...(appt.patients || {}), ...(patient || {}) }}
          existingInvoices={invoices}
          onClose={() => setInvModal(false)}
          onSaved={inv => setInvoices(v => [inv, ...v])}
        />
      )}

      {/* ── Prescription Rx modal ── */}
      {rxModal !== null && (
        <RxModal
          appt={appt}
          patient={patient}
          prefill={rxModal}
          onClose={() => setRxModal(null)}
          onSaved={note => {
            // Only update visit notes list — do NOT close modal (RxModal manages its own toast)
            if (note) setVisitNotes(n => [note, ...n])
          }}
        />
      )}
    </>
  )
}

// ─── Appointment block — prototype colours & style ─────────────────────────────
function ApptBlock({ appt, onClick, compact }) {
  const [hover, setHover] = useState(false)
  const s = getStatus(appt.status)
  const name = appt.patients?.full_name || 'Patient'
  const initials = avatarInitials(name)
  const svcInitial = (appt.service_type || 'S')[0].toUpperCase()
  const isCancelled = appt.status === 'Cancelled' || appt.status === 'cancelled'

  return (
    <div
      onClick={e => { e.stopPropagation(); onClick() }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        borderRadius: 5,
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        borderLeft: `3px solid ${s.border}`,
        background: s.bg,
        opacity: isCancelled ? 0.75 : 1,
        transition: 'transform .1s, box-shadow .1s',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 3px 10px rgba(0,0,0,.12)' : 'none',
        position: 'relative', overflow: 'hidden', zIndex: 2,
        ...(compact
          ? { flex: 1, minWidth: 0, alignSelf: 'stretch', margin: 0, padding: '3px 6px' }
          : { margin: '1px 1px 2px', padding: '4px 8px', minHeight: 0 }
        ),
      }}
    >
      {/* Patient name */}
      <div style={{ fontSize: compact ? 11 : 12, fontWeight: 700, color: s.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 16, height: 16, borderRadius: 4, background: s.border + '44', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: s.color, flexShrink: 0 }}>{initials}</span>
        {name}
      </div>
      {/* Service + time */}
      <div style={{ fontSize: compact ? 10 : 10.5, marginTop: 1, opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: s.color }}>
        {fmtTime(appt.time)} · {appt.service_type}
      </div>
      {/* Service initial badge */}
      {!compact && (
        <div style={{ position: 'absolute', top: 3, right: 4, width: 16, height: 16, borderRadius: 3, background: 'rgba(0,0,0,.08)', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color, opacity: 0.7, textTransform: 'uppercase' }}>
          {svcInitial}
        </div>
      )}
    </div>
  )
}

// ─── Live current-time rule ────────────────────────────────────────────────────
// Uses fraction-based `top` so position is correct regardless of dynamic row height.
// The parent body div's height = TIME_SLOTS.length × ROW_H, so `top: X%` maps
// fraction directly to the right pixel without needing to know ROW_H in JS.
function TimeRule() {
  const [pct, setPct] = useState(null)   // null = outside working hours → hide

  useEffect(() => {
    function calc() {
      const now = new Date()                              // device local time (IST etc.)
      const minutes = now.getHours() * 60 + now.getMinutes() - toMins(CALENDAR_RANGE.start)
      const totalMin = TIME_SLOTS.length * CLINIC_HOURS.slotMinutes
      if (minutes < 0 || minutes >= totalMin) { setPct(null); return }
      setPct((minutes / totalMin) * 100)                  // 0–100 %
    }
    calc()
    const id = setInterval(calc, 60_000)
    return () => clearInterval(id)
  }, [])

  if (pct === null) return null
  return (
    <div style={{ position: 'absolute', left: TIME_W, right: 0, top: `${pct.toFixed(4)}%`, height: 0, borderTop: '1.5px dashed rgba(239, 68, 68, 0.5)', pointerEvents: 'none', zIndex: 1 }}>
      <div style={{ position: 'absolute', left: -5, top: -4, width: 8, height: 8, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.6)' }} />
    </div>
  )
}

// ─── List view ────────────────────────────────────────────────────────────────
function ListView({ appointments, onSelect, onBook }) {
  // Group by date, sort dates then appointments by time within each date
  const grouped = {}
  appointments.forEach(a => {
    if (!a.date) return
    if (!grouped[a.date]) grouped[a.date] = []
    grouped[a.date].push(a)
  })
  const sortedDates = Object.keys(grouped).sort()

  if (sortedDates.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: T.gray400, fontSize: 14 }}>
        <span style={{ fontSize: 36 }}>📋</span>
        No appointments this week
      </div>
    )
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: T.gray50 }}>
      {sortedDates.map(date => {
        const dayAppts = [...grouped[date]].sort((a, b) => (a.time || '') < (b.time || '') ? -1 : 1)
        const dateObj  = new Date(date + 'T00:00:00')
        const dateLabel = dateObj.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })
        const isTdy    = date === isoDate(new Date())
        return (
          <div key={date}>
            {/* Date group header */}
            <div style={{
              padding: '10px 20px 7px', fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.8px',
              color: isTdy ? T.teal700 : T.gray500,
              background: isTdy ? T.teal50 : T.gray100,
              borderBottom: `1px solid ${isTdy ? T.teal100 : T.gray200}`,
              borderTop: `2px solid ${isTdy ? T.teal400 : T.gray200}`,
              position: 'sticky', top: 0, zIndex: 10,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {isTdy && <span style={{ background: T.teal500, color: '#fff', fontSize: 9.5, fontWeight: 800, padding: '1px 7px', borderRadius: 10, letterSpacing: '.5px' }}>TODAY</span>}
              {dateLabel}
              <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 500, color: T.gray400 }}>{dayAppts.length} appt{dayAppts.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Appointment rows */}
            {dayAppts.map(a => {
              const s    = getStatus(a.status)
              const name = a.patients?.full_name || 'Patient'
              return (
                <div key={a.id}
                  onClick={() => onSelect(a)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '11px 20px', borderBottom: `1px solid ${T.gray100}`,
                    background: T.white, cursor: 'pointer', transition: 'background .1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = T.teal50}
                  onMouseLeave={e => e.currentTarget.style.background = T.white}
                >
                  {/* Time */}
                  <div style={{ width: 52, fontSize: 11.5, fontWeight: 700, color: T.gray400, flexShrink: 0, lineHeight: 1.3 }}>
                    {fmtTime(a.time)}
                  </div>
                  {/* Status stripe */}
                  <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: s.border, flexShrink: 0 }} />
                  {/* Avatar */}
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: s.bg, border: `1.5px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: s.color, flexShrink: 0 }}>
                    {avatarInitials(name)}
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: T.gray900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                    <div style={{ fontSize: 11.5, color: T.gray500, marginTop: 1 }}>
                      {a.service_type}{a.doctor ? ` · ${a.doctor}` : ''}
                    </div>
                  </div>
                  {/* Status badge */}
                  <span style={{ padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.border}`, flexShrink: 0 }}>
                    {s.label}
                  </span>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN APPOINTMENTS MODULE
// ═══════════════════════════════════════════════════════════════════════════════
export default function Appointments() {
  const { isAdmin } = useAuth()
  // Open the booking modal for a grid cell. Only the past is blocked (admin: "Record a past visit").
  function openCell(date, time) {
    const past = time ? isPastSlot(date, time) : date < todayIST()
    if (past && !isAdmin) return
    setBooking({ date, time, past })
  }
  const [weekStart, setWeekStart]  = useState(() => getMondayOfWeek(new Date()))
  const [dayView, setDayView]      = useState(() => new Date())   // selected day for Day view
  const [appointments, setAppts]   = useState([])
  const [loading, setLoading]      = useState(true)
  const [bookingModal, setBooking] = useState(null)
  const [profilePanel, setProfile] = useState(null)
  const [editAppt, setEditAppt]    = useState(null)
  const [view, setView]            = useState('week')
  const [activeFilter, setFilter]  = useState('Total')
  const scrollRef = useRef(null)

  const weekDays = getWeekDays(weekStart)
  const weekEnd  = weekDays[6]

  // Which day columns to render — day view shows just one column
  const visibleDays = view === 'day' ? [dayView] : weekDays

  // Filtered appointments — drives both calendar and filter list panel
  const filteredAppts = activeFilter === 'Total'
    ? appointments
    : appointments.filter(a => a.status === activeFilter)

  const apptMap = useMemo(() => {
    const map = {}
    filteredAppts.forEach(a => {
      if (!a.date || !a.time) return
      const slot = timeToSlot(a.time)
      if (!slot) return
      const key = `${a.date}|${slot}`
      if (!map[key]) map[key] = []
      map[key].push(a)
    })
    return map
  }, [filteredAppts])

  const load = useCallback(async () => {
    setLoading(true)
    // In day view query just the selected day; week/list queries the full week.
    const rangeStart = view === 'day' ? isoDate(dayView) : isoDate(weekStart)
    const rangeEnd   = view === 'day' ? isoDate(dayView) : isoDate(weekEnd)
    const { data } = await supabase
      .from('appointments')
      .select('*, patients(full_name, phone)')
      .gte('date', rangeStart)
      .lte('date', rangeEnd)
      .order('time', { ascending: true })
    setAppts(data || [])
    setLoading(false)
  }, [weekStart, view, dayView]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load() }, [load])

  // Navigation — step by day in Day view, by week in Week/List view
  function prevPeriod() {
    if (view === 'day') {
      const n = new Date(dayView); n.setDate(n.getDate() - 1)
      setDayView(n); setWeekStart(getMondayOfWeek(n))
    } else {
      setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n })
    }
  }
  function nextPeriod() {
    if (view === 'day') {
      const n = new Date(dayView); n.setDate(n.getDate() + 1)
      setDayView(n); setWeekStart(getMondayOfWeek(n))
    } else {
      setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n })
    }
  }
  function handleSaved(appt) { setAppts(prev => [...prev, appt]); setBooking(null) }
  function handleUpdated(appt) { setAppts(prev => prev.map(a => a.id === appt.id ? appt : a)); setProfile(appt) }
  function handleDeleted(id)  { setAppts(prev => prev.filter(a => a.id !== id)); setProfile(null) }

  // Stats for the dark bar
  const statsTotal = appointments.length
  const statsMap   = Object.fromEntries(ALL_STATUSES.map(s => [s, appointments.filter(a => a.status === s).length]))

  const dateRangeLabel = (() => {
    if (view === 'day')
      return dayView.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    const s = weekStart, e = weekEnd
    if (s.getMonth() === e.getMonth())
      return `${s.toLocaleDateString('en-IN', { day: 'numeric' })}–${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`
    return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
  })()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: T.font, background: T.gray50, overflow: 'hidden' }}>

      {/* ══ TOPBAR — dark teal, prototype-exact ══ */}
      <div style={{ background: T.teal900, flexShrink: 0, boxShadow: '0 2px 12px rgba(0,0,0,.25)', zIndex: 50 }}>
        {/* Row 1 */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px', height: 56, gap: 14 }}>

          {/* Date navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={prevPeriod} style={{ ...topNavArrow }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>‹</button>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: T.white, minWidth: 210, textAlign: 'center' }}>
              {dateRangeLabel}
            </span>
            <button onClick={nextPeriod} style={{ ...topNavArrow }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>›</button>
          </div>

          <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,.12)', flexShrink: 0 }} />

          {/* View toggle */}
          <div style={{ display: 'flex', background: 'rgba(255,255,255,.08)', borderRadius: 7, padding: 2 }}>
            {[['Week', 'week'], ['Today', 'day'], ['List', 'list']].map(([label, val]) => (
              <button key={val} onClick={() => setView(val)} style={{
                padding: '4px 14px', borderRadius: 5, border: 'none',
                background: view === val ? T.teal700 : 'transparent',
                color: view === val ? T.white : 'rgba(255,255,255,.6)',
                fontSize: 12.5, fontWeight: 500, cursor: 'pointer', transition: 'all .12s', fontFamily: T.font,
              }}>{label}</button>
            ))}
          </div>

          <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,.12)', flexShrink: 0 }} />

          {/* Legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 6 }}>
            {[
              { label: 'Confirmed',    color: '#22c55e' },
              { label: 'Waiting',      color: '#eab308' },
              { label: 'In Treatment', color: '#3b82f6' },
              { label: 'Completed',    color: '#94a3b8' },
              { label: 'Cancelled',    color: '#ef4444' },
            ].map(({ label, color }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: 'rgba(255,255,255,.55)', whiteSpace: 'nowrap' }}>
                <div style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }} />
                {label}
              </div>
            ))}
          </div>

          {/* Right side — new appt button */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {loading && <span style={{ fontSize: 12, color: 'rgba(255,255,255,.45)' }}>Loading…</span>}
            <button
              onClick={() => setBooking({ date: todayIST(), time: null })}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', background: T.teal500, color: T.white, border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: T.font, transition: 'background .12s' }}
              onMouseEnter={e => e.currentTarget.style.background = T.teal400}
              onMouseLeave={e => e.currentTarget.style.background = T.teal500}
            >+ New Appointment</button>
          </div>
        </div>
      </div>

      {/* ── Stats bar — clickable filter pills ── */}
      <div style={{
        background: T.teal950, borderTop: '1px solid rgba(255,255,255,.06)',
        padding: '0 20px', height: 38, display: 'flex', alignItems: 'center',
        gap: 6, overflowX: 'auto', flexShrink: 0,
      }}>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.8px', textTransform: 'uppercase', color: 'rgba(255,255,255,.3)', whiteSpace: 'nowrap', marginRight: 4 }}>
          This Week
        </span>

        {/* Total pill */}
        {(() => {
          const isActive = activeFilter === 'Total'
          return (
            <button onClick={() => setFilter('Total')} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '3px 11px', borderRadius: 20, cursor: 'pointer',
              background: isActive ? 'rgba(255,255,255,.28)' : 'rgba(255,255,255,.1)',
              color: 'rgba(255,255,255,.85)',
              border: isActive ? '1.5px solid rgba(255,255,255,.6)' : '1.5px solid transparent',
              fontSize: 11.5, fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap', flexShrink: 0,
              fontFamily: T.font, transition: 'all .12s',
            }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{statsTotal}</span> Total
            </button>
          )
        })()}

        <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,.08)', flexShrink: 0 }} />

        {/* Status pills */}
        {ALL_STATUSES.map(s => {
          const c = STATUS[s]
          const count = statsMap[s] || 0
          const isActive = activeFilter === s
          return (
            <button key={s} onClick={() => setFilter(isActive ? 'Total' : s)} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '3px 11px', borderRadius: 20, cursor: 'pointer',
              background: isActive ? c.sbColor + '33' : c.sbBg,
              color: c.sbColor,
              border: isActive ? `1.5px solid ${c.sbColor}` : '1.5px solid transparent',
              fontSize: 11.5, fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap', flexShrink: 0,
              fontFamily: T.font, transition: 'all .12s',
            }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{count}</span> {c.label}
            </button>
          )
        })}
      </div>

      {/* ── Calendar / List body — view-conditional ── */}
      {view === 'list'
        ? (
          /* ── LIST VIEW ─────────────────────────────────────────── */
          <ListView
            appointments={filteredAppts}
            onSelect={a => setProfile(a)}
            onBook={({ date, time }) => setBooking({ date, time })}
          />
        ) : (
          /* ── WEEK / DAY GRID VIEW ───────────────────────────────── */
          /* Architecture: header and body are SEPARATE elements using the SAME
             gridTemplateColumns string — the only reliable way to align columns.
             visibleDays = 7 cols (week) or 1 col (day). */
          (() => {
            const numCols    = visibleDays.length          // 7 or 1
            const colTpl     = `${TIME_W}px repeat(${numCols}, 1fr)`
            const minW       = TIME_W + DAY_MIN * numCols
            return (
              <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                {loading && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, fontSize: 14, color: T.gray400 }}>
                    Loading appointments…
                  </div>
                )}

                <div style={{ minWidth: minW }}>

                  {/* ── Sticky header row ── */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: colTpl,
                    height: 56, minHeight: 56, position: 'sticky', top: 0, zIndex: 20,
                    background: T.white, borderBottom: `2px solid ${T.gray200}`,
                    boxSizing: 'border-box', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'sticky', left: 0, zIndex: 30, background: T.gray50, borderRight: `2px solid ${T.gray200}`, boxSizing: 'border-box' }} />
                    {visibleDays.map((day, i) => {
                      const today = isToday(day)
                      return (
                        <div key={i} style={{
                          background: today ? T.teal50 : '#e8f4f4',
                          borderRight: `1px solid ${T.gray200}`,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          height: 56, gap: 1, cursor: 'pointer', boxSizing: 'border-box',
                        }}
                          onClick={() => openCell(isoDate(day), null)}
                          onMouseEnter={e => { if (!today) e.currentTarget.style.background = T.teal50 }}
                          onMouseLeave={e => { if (!today) e.currentTarget.style.background = today ? T.teal50 : '#e8f4f4' }}
                        >
                          {/* Day abbreviation — MON, TUE, WED… */}
                          <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: today ? T.teal700 : '#666', lineHeight: 1 }}>
                            {day.toLocaleDateString('en-IN', { weekday: 'short' })}
                          </span>
                          {/* Date number — circle for today */}
                          <span style={{
                            width: today ? 30 : 'auto', height: today ? 30 : 'auto',
                            minWidth: today ? 30 : 0,
                            borderRadius: '50%',
                            background: today ? T.teal600 : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 20, fontWeight: 600,
                            color: today ? T.white : T.gray700,
                            lineHeight: 1,
                          }}>
                            {day.getDate()}
                          </span>
                          {/* Month label — May, Jun… */}
                          <span style={{ fontSize: 10, color: today ? T.teal600 : '#888', lineHeight: 1 }}>
                            {day.toLocaleDateString('en-IN', { month: 'short' })}
                          </span>
                        </div>
                      )
                    })}
                  </div>

                  {/* ── Body rows ── */}
                  {/* Each row = its own grid with colTpl. Height via CSS clamp so all 24
                      rows fill (100vh − 192px) exactly: available / 24 = ROW_H */}
                  <div style={{ position: 'relative' }}>
                    <TimeRule />
                    {TIME_SLOTS.map(slot => {
                      const isHour = slot.endsWith(':00')
                      return (
                        <div key={slot} style={{
                          display: 'grid', gridTemplateColumns: colTpl,
                          height: ROW_H, minHeight: ROW_H,
                          borderTop: `1px solid ${isHour ? T.gray200 : T.gray100}`,
                          boxSizing: 'border-box',
                        }}>
                          {/* Time label */}
                          <div style={{
                            position: 'sticky', left: 0, zIndex: 10,
                            background: T.gray50, borderRight: `2px solid ${T.gray200}`,
                            display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
                            paddingRight: 10, paddingTop: 4,
                            overflow: 'hidden', boxSizing: 'border-box',
                          }}>
                            {isHour && <span style={{ fontSize: 11, fontWeight: 600, color: T.gray400 }}>{fmtTime(slot)}</span>}
                            {!isHour && slotLabel(slot) && slot.endsWith(':30') && (
                              <span style={{ fontSize: 8.5, color: T.gray300, fontStyle: 'italic' }}>{slotLabel(slot)}</span>
                            )}
                          </div>

                          {/* Day cells */}
                          {visibleDays.map((day, colIdx) => {
                            const cellKey   = `${isoDate(day)}|${slot}`
                            const cellAppts = apptMap[cellKey] || []
                            const dayIso    = isoDate(day)
                            const past      = isPastSlot(dayIso, slot)
                            const afterHrs  = !!slotLabel(slot)         // informational only — never blocks
                            const blocked   = past && !isAdmin
                            const todayCell = dayIso === todayIST()
                            const cellBg    = past ? '#eef0f3' : todayCell ? '#f3fffe' : T.white
                            const isMulti   = cellAppts.length > 1
                            return (
                              <div key={colIdx}
                                onClick={() => openCell(dayIso, slot)}
                                title={past ? (isAdmin ? 'Past time — record a past visit' : 'Past time') : afterHrs ? `${fmtTime(slot)} (${slotLabel(slot)})` : undefined}
                                style={{
                                  borderRight: `1px solid ${T.gray200}`,
                                  background: cellBg,
                                  padding: '2px 2px 1px', cursor: blocked ? 'not-allowed' : 'pointer',
                                  transition: 'background .1s',
                                  overflow: 'hidden', position: 'relative',
                                  boxSizing: 'border-box',
                                  outline: afterHrs && !past ? '1px dashed #e5e7eb' : undefined, outlineOffset: -2,
                                }}
                                onMouseEnter={e => { if (!blocked) e.currentTarget.style.background = past ? '#e2e8f0' : T.teal50 }}
                                onMouseLeave={e => { e.currentTarget.style.background = cellBg }}
                              >
                                {isMulti ? (
                                  <div style={{ display: 'flex', gap: 2, height: 'calc(100% - 2px)' }}>
                                    {cellAppts.map(a => (
                                      <ApptBlock key={a.id} appt={a} compact onClick={() => setProfile(a)} />
                                    ))}
                                  </div>
                                ) : (
                                  cellAppts.map(a => (
                                    <ApptBlock key={a.id} appt={a} onClick={() => setProfile(a)} />
                                  ))
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })}
                  </div>

                </div>
              </div>
            )
          })()
        )
      }

      {/* ── Filter drawer — slides in from right, overlays calendar ── */}
      {(() => {
        const open = activeFilter !== 'Total'
        const c    = open ? STATUS[activeFilter] : null
        const sorted = open
          ? [...filteredAppts].sort((a, b) => (a.date + (a.time || '')).localeCompare(b.date + (b.time || '')))
          : []
        return (
          <>
            {/* Backdrop — click to close */}
            <div
              onClick={() => setFilter('Total')}
              style={{
                position: 'fixed', inset: 0, zIndex: 200,
                background: 'rgba(0,0,0,0.10)',
                opacity: open ? 1 : 0,
                pointerEvents: open ? 'all' : 'none',
                transition: 'opacity 0.25s ease',
              }}
            />

            {/* Drawer */}
            <div style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: 320, zIndex: 201,
              background: T.white,
              boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
              display: 'flex', flexDirection: 'column',
              transform: open ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.25s ease',
              fontFamily: T.font,
            }}>
              {/* Drawer header */}
              <div style={{
                padding: '14px 16px 12px',
                borderBottom: `1px solid ${T.gray100}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: c ? c.bg : T.gray50, flexShrink: 0,
              }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: c ? c.color : T.gray500 }}>
                    {c ? c.label : ''}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.gray700, marginTop: 2 }}>
                    {sorted.length} appointment{sorted.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <button onClick={() => setFilter('Total')} style={{
                  width: 28, height: 28, borderRadius: 7,
                  background: 'rgba(0,0,0,0.07)', border: 'none',
                  cursor: 'pointer', fontSize: 16, color: T.gray500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: T.font,
                }}>×</button>
              </div>

              {/* Drawer rows */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {open && sorted.length === 0 && (
                  <div style={{ padding: '32px 20px', textAlign: 'center', color: T.gray400, fontSize: 13 }}>
                    No {c.label.toLowerCase()} appointments this period.
                  </div>
                )}
                {open && sorted.map(a => {
                  const name     = a.patients?.full_name || a.patient_name || '—'
                  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                  return (
                    <div key={a.id}
                      onClick={() => { setProfile(a); setFilter('Total') }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 16px', cursor: 'pointer',
                        borderBottom: `1px solid ${T.gray100}`,
                        transition: 'background .1s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = T.teal50}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: c.sbBg, border: `2px solid ${c.border || c.sbColor}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 800, color: c.color,
                      }}>{initials}</div>
                      {/* Name + date/time */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.gray900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                        <div style={{ fontSize: 11, color: T.gray400, marginTop: 2 }}>
                          {fmtDate(a.date)}{a.time ? ' · ' + fmtTime(a.time) : ''}
                        </div>
                        <div style={{ fontSize: 11, color: T.gray500, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.service_type || a.appointment_type || '—'}
                        </div>
                      </div>
                      {/* Status badge */}
                      <span style={{
                        padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                        background: c.bg, color: c.color, border: `1px solid ${c.border}`,
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>{c.label}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )
      })()}

      {/* ── Modals / Panels ── */}
      {bookingModal && (
        <BookingModal
          initialDate={bookingModal.date}
          initialTime={bookingModal.time}
          initialPast={!!bookingModal.past}
          onClose={() => setBooking(null)}
          onSaved={handleSaved}
          allAppointments={appointments}
        />
      )}
      {editAppt && (
        <BookingModal
          editAppt={editAppt}
          onClose={() => setEditAppt(null)}
          onSaved={data => { handleUpdated(data); setEditAppt(null) }}
          allAppointments={appointments}
        />
      )}
      {profilePanel && (
        <ProfilePanel
          appt={profilePanel}
          onClose={() => setProfile(null)}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
          onEdit={a => { setProfile(null); setEditAppt(a) }}
        />
      )}
    </div>
  )
}

// ROW_H: fixed 60 px per time-slot row. Grid scrolls vertically.
const ROW_H = '44px'

// ─── Shared style objects ──────────────────────────────────────────────────────
// Topbar nav arrows — dark version for the teal-900 header
const topNavArrow = {
  width: 28, height: 28, border: '1px solid rgba(255,255,255,.18)', borderRadius: 6,
  background: 'transparent', color: '#fff', fontSize: 16, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'background .12s', fontFamily: T.font,
}
